// THE Office.js seam (PLAN-agent-v2 #12): every `Excel.run` of the add-in lives in this file, one
// method per client tool of the catalogue (§4.3) plus a few helpers (selection, context, custom
// properties, journal snapshots). Everything else is host-agnostic and mocks this class.
//
// Conventions:
//  - inputs carry `sheet` + a bare A1 `address` (#14); a `Sheet!A1` prefix is tolerated;
//  - every method returns a plain JSON object (§10.4): writes include `_changes`, failures return
//    `{error, code, retryable, hint}` via toToolError (#15) and never throw;
//  - results are size-capped (#10) BEFORE touching Office.js when the cap can be computed from
//    the address, and after loading otherwise;
//  - one `Excel.run` per tool.
import type * as X from '../../types/excel';
import type * as A from '../../types/audit';
import type { AgentContext, AuditView, NamedItemRef, RangeAttachment, SelectionSnapshot, SheetInfo, TermsView } from '../../types/agent';
import { officePlatform } from '../office/platform';
import { areasCellCount, boundingBox, cellCount, checkShape, colToLetter, copyDestination, isMultiArea, isValidAddress, parseAreas, parseCell, parseRange, qualify, r1c1Problem, rangeShape, rangeToA1, splitSheetPrefix, stripSheet, wholeColumnsAddress, wholeRowsAddress } from './a1';
import { invalidInput, toToolError, tooLarge } from './officeErrors';
import { areasCells, areasContain, expandArea, fileExtensionOf, isBrokenName, labelFromLeft, nameOnSeries } from './integrity';
import { AUDIT_HEADER_FILL, AUDIT_SHEET_DEFAULT_NAME, layoutAuditSheet } from './auditSheet';
import { FINDINGS_HEADER_FILL, FINDINGS_SHEET_NAME, findingsSheetEdits, layoutFindingsSheet } from './findingsSheet';
import type { FindingsSheetLayout } from './findingsSheet';
import { TERMS_HEADER_FILL, TERMS_SHEET_NAME, layoutTermsSheet } from './termsSheet';
import { DEFAULT_MAX_ADDRESSES_PER_CELL, batchLevels, capAddresses, failedEntry, resolveBatchCells, shapeBatch, type BatchCellRef, type BatchCellResult, type TraceKind } from './precedentsBatch';
import { isOccupied, occupiedCells, occupiedError, overwriteAckError, overwritePreview } from './occupancy';
import { fingerprintSheet } from './fingerprint';
import { encodeCells, encodeValues, expandSpans, groupValidations, halves, isMixedValidation, validationType, type CellBox, type GridStyles } from '../workbook/indexPayload';
import type { IndexCell, IndexSheetPayload, IndexSheetValues, IndexValidation } from '../../types/index';
import type { IndexSheetListing } from '../workbook/WorkbookIndexer';
import { justificationNote, legacyProblems } from './legacy';
import { readPage } from './readPage';
import { ANCHOR_MAX_OBJECTS, anchorLines, anchorObjects, anchorsOf, InvalidManifestError, MANIFEST_MAX_CHUNK_ROWS, manifestTextFromColumn, parseManifestText, PCP_SYNC_SHEET, type AnchorLine } from './syncManifest';
import { detectTimeline, emptyScan, hasExternalRef, hiddenSpans, scanGrid, SCAN_DEFAULT_ROWS, SCAN_HEAD_ROWS, SCAN_MAX_COLS, SCAN_MAX_ROWS, type ScanGrid } from './scan';
import { cellA1, errorCells, layoutBlock, validateBlock } from './block';
import { assess } from './convergence';
import { apiNotSupportedError, fillError, filledTemplateProblem, insertModelProblem, insertModelResult, INSERT_MODEL_API, invalidResponseError, nonEmptyReasons, notConfiguredError, parseCellRef, placeholderRenames, workbookNotEmptyError, type WorkbookContent } from './insertModel';
import { asText, asTextGrid } from './plainText';
import type { RangeSnapshot } from '../agent/ChangeJournal';

export const DEFAULT_MAX_CELLS = 2000;
export const HARD_MAX_CELLS = 10_000;
export const MAX_FIND_RESULTS = 200;
/** Scattered cells accepted by `excel_write_cells` in one call — mirrors the server's WriteCellsInput cap. */
export const MAX_WRITE_CELLS = 200;
export const SELECTION_SNAPSHOT_MAX_CELLS = 30;
const MAX_ERROR_CELLS = 200;
const MAX_HEADER_CELLS = 50;
const MAX_PRECEDENT_ADDRESSES = 200;
/** Audit tools (AUDIT_SPEC §2.1): caps on the Office.js reads. */
export const CAPTURE_MAX_CELLS_PER_PAGE = 30_000;
/** Whole-sheet index capture: cells per Office.js load (a sheet above it is read in row bands, still whole). */
export const INDEX_BAND_CELLS = 100_000;
/** Columns from the left edge of the used range whose cell styles (bold, indent, font colour, fill) are read: the
 *  label area. Styles are per-cell objects, so the timeline columns are left out. */
export const INDEX_STYLE_COLS = 12;
export const EXTERNAL_SCAN_MAX_CELLS = 200_000;
const MAX_HIDDEN_FLAG_ROWS = 50_000;
/** Ranges probed for their validation rule in one sheet: an area under several rules is cut in halves until each
 *  half is under one, at most this many probes in all (past them, the rest is left out). */
const MAX_VALIDATION_PROBES = 400;
const FORMULA_HIDDEN_SAMPLE_CELLS = 200;
const MAX_INSPECT_PRECEDENTS = 200;
const LABEL_LOOKBACK_COLS = 10;

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Requirement-set guard that never throws (Office is absent under jsdom). Every API newer than the
 *  manifest minimum (ExcelApi 1.15) must pass through here AND a try/catch, so a tool degrades
 *  (null / [] / false) instead of failing. */
function apiSupported(version: string): boolean {
  try {
    return !!Office?.context?.requirements?.isSetSupported('ExcelApi', version);
  } catch {
    return false;
  }
}

/** Hidden row / column spans of a range (`getRowProperties` / `getColumnProperties`, ExcelApi 1.9) in one
 *  gated sync, however tall the range: `[]` when unsupported or refused by the host. */
async function hiddenSpansOf(ctx: Excel.RequestContext, range: Excel.Range, rowIndex: number, colIndex: number): Promise<{ rows: string[]; cols: string[] }> {
  if (!apiSupported('1.9')) return { rows: [], cols: [] };
  try {
    const rp = range.getRowProperties({ rowHidden: true });
    const cp = range.getColumnProperties({ columnHidden: true });
    await ctx.sync();
    return {
      rows: hiddenSpans(rp.value.map((r) => !!r.rowHidden), rowIndex + 1, 'row'),
      cols: hiddenSpans(cp.value.map((c) => !!c.columnHidden), colIndex + 1, 'col'),
    };
  } catch {
    return { rows: [], cols: [] };
  }
}

/** The data validation rules over a used range, for the index: the cells under one (`getSpecialCells`, ExcelApi
 *  1.9), area by area, an area under several rules cut in halves until each half is under one (a type sync, then a
 *  sync for the lists' sources). Gated and guarded: a failure costs the rules, not the sheet. */
async function validationsOf(ctx: Excel.RequestContext, ws: Excel.Worksheet, used: Excel.Range): Promise<IndexValidation[]> {
  if (!apiSupported('1.9')) return [];
  try {
    const special = used.getSpecialCellsOrNullObject(Excel.SpecialCellType.dataValidations);
    special.load('areas/items/rowIndex,areas/items/columnIndex,areas/items/rowCount,areas/items/columnCount');
    await ctx.sync();
    if (special.isNullObject) return [];
    const queue: CellBox[] = special.areas.items.map((a) => ({ row: a.rowIndex, col: a.columnIndex, rows: a.rowCount, cols: a.columnCount }));
    const pieces: { address: string; type: string; source: string | null }[] = [];
    let probes = 0;
    while (queue.length && probes < MAX_VALIDATION_PROBES) {
      const boxes = queue.splice(0, MAX_VALIDATION_PROBES - probes);
      probes += boxes.length;
      const ranges = boxes.map((b) => {
        const r = ws.getRangeByIndexes(b.row, b.col, b.rows, b.cols);
        r.load('address');
        r.dataValidation.load('type');
        return r;
      });
      await ctx.sync();
      const lists: { range: Excel.Range; address: string }[] = [];
      ranges.forEach((r, i) => {
        const office = String(r.dataValidation.type);
        if (isMixedValidation(office)) {
          queue.push(...(halves(boxes[i]) ?? []));
          return;
        }
        const type = validationType(office);
        if (type === 'list') {
          r.dataValidation.load('rule');
          lists.push({ range: r, address: stripSheet(r.address) });
        } else if (type) {
          pieces.push({ address: stripSheet(r.address), type, source: null });
        }
      });
      if (lists.length) await ctx.sync();
      for (const { range, address } of lists) {
        const source = range.dataValidation.rule.list?.source;     // loaded, a formula or the options as text
        pieces.push({ address, type: 'list', source: typeof source === 'string' ? source : null });
      }
    }
    return groupValidations(pieces);
  } catch {
    return [];
  }
}

/** Row hidden flags of a range (one per row), gated; `[]` when unavailable. */
async function rowHiddenFlags(ctx: Excel.RequestContext, range: Excel.Range): Promise<boolean[]> {
  if (!apiSupported('1.9')) return [];
  try {
    const rp = range.getRowProperties({ rowHidden: true });
    await ctx.sync();
    return rp.value.map((r) => !!r.rowHidden);
  } catch {
    return [];
  }
}

/** A sheet's fingerprint (`fingerprint.ts`): the `formulas` of the whole used range, read in row bands of
 *  INDEX_BAND_CELLS (one sync each), the hidden rows and columns and the table / chart counts. Every caller —
 *  the turn context, the index upload, the undo journal — goes through here, so they always agree. Throws on an
 *  Office error: the caller decides what a sheet without a fingerprint means. */
async function readFingerprint(ctx: Excel.RequestContext, ws: Excel.Worksheet): Promise<string> {
  const used = ws.getUsedRangeOrNullObject(true);
  used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
  const tables = ws.tables;
  tables.load('count');
  const charts = ws.charts;
  charts.load('count');
  await ctx.sync();
  if (used.isNullObject) {
    return fingerprintSheet({ usedRange: '', cellCount: 0, formulas: [], hiddenRows: [], hiddenCols: [], tableCount: tables.count, chartCount: charts.count });
  }
  const { rowIndex, columnIndex, rowCount: rows, columnCount: cols } = used;
  const rowProps = used.getRowProperties({ rowHidden: true });
  const colProps = used.getColumnProperties({ columnHidden: true });
  const formulas: unknown[][] = [];
  const bandRows = Math.max(1, Math.floor(INDEX_BAND_CELLS / Math.max(1, cols)));
  for (let r0 = 0; r0 < rows; r0 += bandRows) {
    const band = ws.getRangeByIndexes(rowIndex + r0, columnIndex, Math.min(bandRows, rows - r0), cols);
    band.load('formulas');
    await ctx.sync();
    for (const row of band.formulas as unknown[][]) formulas.push(row);
  }
  return fingerprintSheet({
    usedRange: stripSheet(used.address),
    cellCount: rows * cols,
    formulas,
    hiddenRows: hiddenSpans(rowProps.value.map((r) => !!r.rowHidden), rowIndex + 1, 'row'),
    hiddenCols: hiddenSpans(colProps.value.map((c) => !!c.columnHidden), columnIndex + 1, 'col'),
    tableCount: tables.count,
    chartCount: charts.count,
  });
}

/** Fingerprints of `sheets`, one after the other; a sheet whose read fails gets null (logged). */
async function readFingerprints(ctx: Excel.RequestContext, sheets: Excel.Worksheet[]): Promise<(string | null)[]> {
  const out: (string | null)[] = [];
  for (const ws of sheets) {
    try {
      out.push(await readFingerprint(ctx, ws));
    } catch (err) {
      console.warn(`[agent-v2] fingerprint of sheet ${ws.name} unavailable`, err);
      out.push(null);
    }
  }
  return out;
}

type Json = Record<string, unknown>;

function changes(sheet: string, address: string): X.Changes {
  return { sheet, address, cells: cellCount(address) };
}

/** `_changes` for a (possibly multi-area) address: the areas joined back with commas, total cells. */
function areaChanges(sheet: string, areas: string[]): X.Changes {
  return { sheet, address: areas.join(','), cells: areasCellCount(areas) };
}

/** Parse a possibly multi-area address into normalised areas, or return an InvalidInput result. */
function areasOrError(address: string): string[] | Json {
  try {
    return parseAreas(address);
  } catch (e) {
    return invalidInput((e as Error).message);
  }
}

/** A Range (single area) or a RangeAreas (several areas) — both expose format / numberFormat / clear / select. */
function rangeOrAreas(ws: Excel.Worksheet, areas: string[]): Excel.Range | Excel.RangeAreas {
  return areas.length === 1 ? ws.getRange(areas[0]) : ws.getRanges(areas.join(','));
}

function clampMaxCells(v: number | undefined): number {
  if (!v || !Number.isFinite(v) || v < 1) return DEFAULT_MAX_CELLS;
  return Math.min(Math.floor(v), HARD_MAX_CELLS);
}

/** Excel character width → points (what Office.js `columnWidth` expects). */
function charWidthToPoints(width: number): number {
  return Math.round(width * 7 + 5) * 0.75;
}

/** Summarise `valueTypes` into counts, e.g. {Double: 12, String: 3, Empty: 5, Error: 1}. */
function summariseTypes(types: string[][]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of types) for (const t of row) out[t] = (out[t] ?? 0) + 1;
  return out;
}

function chartTypeOf(t: X.ChartType): string {
  switch (t) {
    case 'line': return 'Line';
    case 'line_markers': return 'LineMarkers';
    case 'column':
    case 'column_clustered': return 'ColumnClustered';
    case 'column_stacked': return 'ColumnStacked';
    case 'bar':
    case 'bar_clustered': return 'BarClustered';
    case 'bar_stacked': return 'BarStacked';
    case 'area': return 'Area';
    case 'area_stacked': return 'AreaStacked';
    case 'pie': return 'Pie';
    case 'doughnut': return 'Doughnut';
    case 'scatter': return 'XYScatter';
    case 'waterfall': return 'Waterfall';
    case 'combo': return 'ColumnClustered'; // Office.js has no combo type: `secondary_series` are switched to lines afterwards
    default: return 'ColumnClustered';
  }
}

const LEGEND_POSITIONS: Record<Exclude<X.LegendPosition, 'none'>, string> = { top: 'Top', bottom: 'Bottom', left: 'Left', right: 'Right' };

/** Apply the optional styling fields (C6b) to a chart inside an Excel.run. Every property is set on its
 *  own so one unsupported/rejected property never discards the others; failures are reported in `notes`
 *  (the tool result carries them back to the agent). Must be followed by a ctx.sync() by the caller. */
async function applyChartStyle(ctx: Excel.RequestContext, chart: Excel.Chart, input: X.ChartStyleFields, notes: string[]): Promise<void> {
  const attempt = async (label: string, fn: () => void | Promise<void>): Promise<void> => {
    try {
      await fn();
      await ctx.sync();
    } catch (err) {
      notes.push(`${label}: not applied (${err instanceof Error ? err.message : String(err)})`);
    }
  };
  if (input.size) await attempt('size', () => { chart.width = input.size!.width; chart.height = input.size!.height; });
  if (input.legend_position != null) {
    await attempt('legend_position', () => {
      if (input.legend_position === 'none') chart.legend.visible = false;
      else { chart.legend.visible = true; chart.legend.position = LEGEND_POSITIONS[input.legend_position as Exclude<X.LegendPosition, 'none'>] as Excel.ChartLegendPosition; }
    });
  }
  if (input.axis_titles) {
    const at = input.axis_titles;
    if (at.category != null) await attempt('axis_titles.category', () => { const ax = chart.axes.categoryAxis; ax.title.visible = !!at.category; if (at.category) ax.title.text = at.category; });
    if (at.value != null) await attempt('axis_titles.value', () => { const ax = chart.axes.valueAxis; ax.title.visible = !!at.value; if (at.value) ax.title.text = at.value; });
  }
  if (input.value_number_format) await attempt('value_number_format', () => { const ax = chart.axes.valueAxis; ax.numberFormat = input.value_number_format as string; });
  if (input.gridlines != null) await attempt('gridlines', () => { chart.axes.valueAxis.majorGridlines.visible = !!input.gridlines; });
  if (input.secondary_series && input.secondary_series.length) {
    await attempt('secondary_series', () => {
      for (const i of input.secondary_series as number[]) {
        const s = chart.series.getItemAt(i);
        s.chartType = 'Line' as Excel.ChartType;
        s.axisGroup = 'Secondary' as Excel.ChartAxisGroup;
      }
    });
  }
  if (input.series_colors && input.series_colors.length) {
    await attempt('series_colors', () => {
      input.series_colors!.forEach((color, i) => {
        const s = chart.series.getItemAt(i);
        s.format.fill.setSolidColor(color);
        s.format.line.color = color;
      });
    });
  }
  if (input.gap_width != null) {
    // gapWidth lives on each column/bar series: set it on all of them.
    await attempt('gap_width', async () => {
      const series = chart.series;
      series.load('items');
      await ctx.sync();
      for (const s of series.items) s.gapWidth = input.gap_width as number;
    });
  }
  if (input.data_labels != null) {
    await attempt('data_labels', async () => {
      const series = chart.series;
      series.load('items');
      await ctx.sync();
      for (const s of series.items) s.hasDataLabels = !!input.data_labels;
    });
  }
}

/** Resolve a chart's data location: an explicit `data_sheet`, or a `Sheet!range` prefix on the range
 *  (backend leniency mirrored client-side), else the chart's own sheet. Returns the areas. */
type ChartData = { ok: true; sheet: string; areas: string[] } | { ok: false; error: Json };
function chartDataOf(input: { sheet: string; data_sheet?: string | null; data_range: string }): ChartData {
  const split = splitSheetPrefix(input.data_range);
  const sheet = input.data_sheet || split.sheet || input.sheet;
  const areas = areasOrError(split.address);
  if (!Array.isArray(areas)) return { ok: false, error: areas };
  return { ok: true, sheet, areas };
}

/** Build a chart's series from several non-adjacent areas: the chart is created from the first area
 *  (which also provides the categories), then every further area becomes one series. Unsupported
 *  operations are reported in `notes` rather than failing the call. */
async function addExtraAreaSeries(ctx: Excel.RequestContext, chart: Excel.Chart, dataWs: Excel.Worksheet, areas: string[], seriesBy: 'auto' | 'rows' | 'columns' | undefined, notes: string[]): Promise<void> {
  for (const area of areas.slice(1)) {
    try {
      const values = dataWs.getRange(area);
      const shape = rangeShape(area);
      const byRows = seriesBy === 'rows' || (seriesBy !== 'columns' && shape.rows === 1);
      // A one-row (or one-column) area is one series; a labelled area uses its first cell as the name.
      const label = values.getCell(0, 0);
      label.load('text');
      await ctx.sync();
      const name = String(label.text?.[0]?.[0] ?? '') || `Series ${chart.series.count ?? ''}`.trim();
      const series = chart.series.add(name);
      const hasLabel = /[A-Za-z]/.test(name);
      const dataRange = hasLabel && shape.cols > 1 && byRows ? values.getOffsetRange(0, 1).getResizedRange(0, -1) : hasLabel && shape.rows > 1 && !byRows ? values.getOffsetRange(1, 0).getResizedRange(-1, 0) : values;
      series.setValues(dataRange);
      await ctx.sync();
    } catch (err) {
      notes.push(`series from ${area}: not added (${err instanceof Error ? err.message : String(err)})`);
    }
  }
  // Category axis values from the first area's header row/column when the first area carried them.
  try {
    const first = dataWs.getRange(areas[0]);
    const shape = rangeShape(areas[0]);
    if (seriesBy === 'rows' && shape.rows > 1) {
      const cats = first.getRow(0).getOffsetRange(0, 1).getResizedRange(0, -1);
      const s0 = chart.series.getItemAt(0);
      s0.setXAxisValues(cats);
      await ctx.sync();
    }
  } catch (err) {
    notes.push(`categories: not set (${err instanceof Error ? err.message : String(err)})`);
  }
}

function seriesByOf(s: 'auto' | 'rows' | 'columns' | undefined): string {
  return s === 'rows' ? 'Rows' : s === 'columns' ? 'Columns' : 'Auto';
}

/** Common loading of a worksheet by exact name (throws ItemNotFound like Office.js would). */
function sheetOf(ctx: Excel.RequestContext, sheet: string): Excel.Worksheet {
  if (!sheet || typeof sheet !== 'string') throw Object.assign(new Error('sheet is required'), { code: 'InvalidArgument' });
  return ctx.workbook.worksheets.getItem(sheet);
}

/**
 * Overwrite guard for cell writes (PLAN-agent-v2 §13 D2). Loads the target's values and formulas in the
 * caller's `Excel.run` (one extra sync) and returns an `OccupiedCells` error when any target cell —
 * outside `exclude` — is non-empty and `overwrite` is not true. Nothing has been written at that point,
 * so the caller just returns the error. Prompt rules alone must not protect the user's cells in
 * `full_auto` mode. `excel_write_block` runs the same detection but refuses with `OverwriteRequiresAck`,
 * which only the user's checkbox lifts (#36) — see occupancy.ts.
 */
async function guardOccupied(ctx: Excel.RequestContext, ws: Excel.Worksheet, sheet: string, address: string, overwrite: boolean | undefined, exclude: string[] = []): Promise<Json | null> {
  if (overwrite === true) return null;
  const target = ws.getRange(address);
  target.load(['values', 'formulas']);
  await ctx.sync();
  const occupied = occupiedCells(target.values as unknown[][], target.formulas as unknown[][], address, exclude);
  return occupied.length ? (occupiedError(sheet, address, occupied) as unknown as Json) : null;
}

/** Everything `excel_insert_model` would lose by deleting the sheets (insertModel.ts decides): values and
 *  formulas, names of both scopes, charts and shapes. Two syncs, whatever the sheet count. */
async function readWorkbookContent(ctx: Excel.RequestContext): Promise<WorkbookContent> {
  const sheets = ctx.workbook.worksheets;
  sheets.load('items/name');
  await ctx.sync();
  const canCountShapes = apiSupported('1.9');
  const per = sheets.items.map((ws) => ({
    name: ws.name,
    used: ws.getUsedRangeOrNullObject(true),
    names: ws.names.getCount(),
    charts: ws.charts.getCount(),
    shapes: canCountShapes ? ws.shapes.getCount() : null,
  }));
  const names = ctx.workbook.names.getCount();
  await ctx.sync();
  return {
    sheets: per.map((s) => ({ name: s.name, hasValues: !s.used.isNullObject, names: s.names.value, charts: s.charts.value, shapes: s.shapes ? s.shapes.value : null })),
    names: names.value,
  };
}

/** The value of each `Sheet!A1` reference (its top-left cell), null when the reference is malformed or its sheet
 *  does not exist. Two syncs. */
async function readCellRefs(ctx: Excel.RequestContext, refs: string[]): Promise<X.CellValue[]> {
  const parsed = refs.map((ref) => parseCellRef(ref));
  const sheets = new Map<string, Excel.Worksheet>();
  for (const p of parsed) {
    if (p && !sheets.has(p.sheet)) {
      const ws = ctx.workbook.worksheets.getItemOrNullObject(p.sheet);
      ws.load('name');
      sheets.set(p.sheet, ws);
    }
  }
  await ctx.sync();
  const ranges = parsed.map((p) => {
    const ws = p ? sheets.get(p.sheet) : undefined;
    if (!p || !ws || ws.isNullObject) return null;
    const cell = ws.getRange(p.address).getCell(0, 0);
    cell.load('values');
    return cell;
  });
  await ctx.sync();
  return ranges.map((r) => (r ? ((r.values?.[0]?.[0] ?? null) as X.CellValue) : null));
}

export class ExcelToolExecutor {
  // ────────────────────────────────────────────────────────────────────────
  // Helpers used by the app (not tools)
  // ────────────────────────────────────────────────────────────────────────

  async getCustomProperty(key: string): Promise<string | null> {
    return Excel.run(async (ctx) => {
      const item = ctx.workbook.properties.custom.getItemOrNullObject(key);
      item.load('value');
      await ctx.sync();
      return item.isNullObject ? null : String(item.value ?? '') || null;
    });
  }

  /** Resolves once the user leaves the cell they are typing in. `delayForCellEdit` has Excel hold the batch until
   *  they press Enter or Esc (ExcelApi 1.11, below the 1.15 minimum): Excel raises no event when an edit ends, and
   *  this is how to wait for it without polling. A trivial load, so the batch is really sent to the host. */
  async waitForCellEditEnd(): Promise<void> {
    await Excel.run({ delayForCellEdit: true }, async (ctx) => {
      ctx.workbook.load('name');
      await ctx.sync();
    });
  }

  async setCustomProperty(key: string, value: string): Promise<void> {
    await Excel.run(async (ctx) => {
      ctx.workbook.properties.custom.add(key, value);
      await ctx.sync();
    });
  }

  /** Document URL / path: the server's known paths decide when to ask which workbook the file is (the identity question). */
  async getDocumentUrl(): Promise<string | null> {
    try {
      const url = Office.context?.document?.url;
      return typeof url === 'string' && url ? url : null;
    } catch {
      return null;
    }
  }

  async readSelectionAddress(): Promise<{ sheet: string; address: string } | null> {
    try {
      return await Excel.run(async (ctx) => {
        // getSelectedRanges (ExcelApi 1.9) also covers multi-area selections (Ctrl/Cmd-click), where
        // getSelectedRange() throws — that used to freeze the chip on the previous selection.
        const areas = ctx.workbook.getSelectedRanges();
        areas.load('address');
        const ws = ctx.workbook.worksheets.getActiveWorksheet();
        ws.load('name');
        await ctx.sync();
        const address = areas.address
          .split(',')
          .map((a) => stripSheet(a.trim()))
          .join(',');
        return { sheet: ws.name, address };
      });
    } catch {
      return null;
    }
  }

  /** Live selection watcher (chat chip). Registers the workbook event AND polls as a fallback:
   *  Office.js event handlers occasionally stop firing (Mac WebView, after long batches), so the
   *  poll keeps the chip honest. Returns a disposer that removes the handler and stops the poll. */
  async onSelectionChanged(cb: (sel: { sheet: string; address: string }) => void, pollMs = 1500): Promise<() => void> {
    let last: string | null = null;
    const emit = (sel: { sheet: string; address: string } | null) => {
      if (!sel) return;
      const key = `${sel.sheet}!${sel.address}`;
      if (key === last) return;
      last = key;
      cb(sel);
    };
    let handler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | null = null;
    try {
      await Excel.run(async (ctx) => {
        handler = ctx.workbook.onSelectionChanged.add(async () => {
          emit(await this.readSelectionAddress());
        });
        await ctx.sync();
      });
    } catch (err) {
      console.warn('[agent-v2] onSelectionChanged registration failed; polling only', err);
    }
    const timer = setInterval(() => {
      void this.readSelectionAddress().then(emit);
    }, pollMs);
    return () => {
      clearInterval(timer);
      if (handler) {
        void Excel.run(async (ctx) => {
          handler!.remove();
          await ctx.sync();
        }).catch(() => undefined);
      }
    };
  }

  /** Selection snapshot for the per-turn context (≤ 30 cells, #11). */
  async readSelection(): Promise<SelectionSnapshot | null> {
    try {
      return await Excel.run(async (ctx) => {
        const range = ctx.workbook.getSelectedRange();
        const ws = range.worksheet;
        range.load(['address', 'rowCount', 'columnCount', 'values', 'formulas']);
        ws.load('name');
        await ctx.sync();
        const address = stripSheet(range.address);
        if (range.rowCount * range.columnCount > SELECTION_SNAPSHOT_MAX_CELLS) {
          return { sheet: ws.name, address, values: [], formulas: [] };
        }
        return {
          sheet: ws.name,
          address,
          values: range.values as unknown[][],
          formulas: (range.formulas as unknown[][]).map((r) => r.map(String)),
        };
      });
    } catch {
      // Multi-area selection (getSelectedRange throws) or edit mode: keep the address, drop the values.
      const sel = await this.readSelectionAddress();
      return sel ? { sheet: sel.sheet, address: sel.address, values: [], formulas: [] } : null;
    }
  }

  /** Cheap workbook context (#11): sheet list + active sheet + calc mode + selection. */
  async buildContext(workbookId: string, locale: string): Promise<AgentContext> {
    const t0 = now();
    const selection = await this.readSelection();
    const context = await Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      wb.load('name');
      const sheets = wb.worksheets;
      sheets.load('items/name,items/visibility');
      const active = wb.worksheets.getActiveWorksheet();
      active.load('name');
      const app = wb.application;
      app.load('calculationMode');
      await ctx.sync();
      const used = sheets.items.map((ws) => {
        const r = ws.getUsedRangeOrNullObject(true);
        r.load(['address', 'rowCount', 'columnCount']);
        return r;
      });
      await ctx.sync();
      const infos: SheetInfo[] = sheets.items.map((ws, i) => {
        const r = used[i];
        const has = !r.isNullObject;
        return {
          name: ws.name,
          visible: ws.visibility === 'Visible',
          visibility: String(ws.visibility).toLowerCase(),
          used_range: has ? stripSheet(r.address) : null,
          rows: has ? r.rowCount : 0,
          cols: has ? r.columnCount : 0,
          fingerprint: null,
        };
      });
      // Per-sheet fingerprints (§11.3, #21), every sheet; a failed one stays null (the server reads it as changed)
      // and never breaks the context.
      (await readFingerprints(ctx, sheets.items)).forEach((fp, i) => {
        infos[i].fingerprint = fp;
      });
      const out: AgentContext = {
        workbook_id: workbookId,
        workbook_name: wb.name,
        active_sheet: active.name,
        sheets: infos,
        selection,
        locale,
        calculation_mode: String(app.calculationMode).toLowerCase(),
        platform: officePlatform(),
      };
      return out;
    });
    // Measurement only (M3 row i): what the context costs client-side; the backend just logs it.
    const fingerprinted = context.sheets.filter((s) => s.fingerprint !== null && s.fingerprint !== undefined);
    context.client_stats = {
      context_ms: Math.round(now() - t0),
      sheets: context.sheets.length,
      fingerprinted: fingerprinted.length,
      fingerprint_cells: fingerprinted.reduce((n, s) => n + s.rows * s.cols, 0),
    };
    console.debug(`[agent-v2] context built in ${Math.round(now() - t0)} ms (${context.sheets.length} sheets)`);
    return context;
  }

  /** Fingerprint of one sheet (§11.3, `readFingerprint`). */
  async readSheetFingerprint(sheet: string): Promise<string> {
    return Excel.run(async (ctx) => readFingerprint(ctx, sheetOf(ctx, sheet)));
  }

  /** Fingerprints of every sheet (`readFingerprint`); null for a sheet whose read failed. */
  async readSheetFingerprints(): Promise<Record<string, string | null>> {
    return Excel.run(async (ctx) => {
      const sheets = ctx.workbook.worksheets;
      sheets.load('items/name');
      await ctx.sync();
      const fps = await readFingerprints(ctx, sheets.items);
      return Object.fromEntries(sheets.items.map((ws, i) => [ws.name, fps[i]]));
    });
  }

  /** Tell `onChange` which sheets the user changed, so the index can catch up before the next message: cell
   *  contents and rows / columns inserted or deleted (`onChanged`), rows shown or hidden, sorts, sheets added or
   *  deleted. Left out: this add-in's own writes (`triggerSource`, which only `onChanged` carries — the agent's
   *  writes reach the index through `markWritten`), and formats, which the fingerprint does not see either and
   *  which the agent's formatting would echo into a second re-index. A deleted sheet reports no name (`[]`):
   *  the sheet list changed. An accelerator only — a missed event is still caught by the fingerprint at the
   *  next message. Resolves to the function that stops listening. */
  async watchSheetChanges(onChange: (sheets: string[]) => void, onFormat?: (sheets: string[]) => void): Promise<() => Promise<void>> {
    const names = new Map<string, string>();   // worksheet id → name, for events that carry only the id
    const refreshNames = async (): Promise<void> => {
      await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/id,items/name');
        await ctx.sync();
        names.clear();
        for (const ws of sheets.items) names.set(ws.id, ws.name);
      });
    };
    await refreshNames();
    const report = async (worksheetId: string): Promise<void> => {
      if (!names.has(worksheetId)) await refreshNames().catch(() => undefined);   // renamed or new since
      const name = names.get(worksheetId);
      onChange(name ? [name] : []);
    };
    const fire = (worksheetId: string) => void report(worksheetId).catch(() => onChange([]));
    const handlers: OfficeExtension.EventHandlerResult<unknown>[] = [];
    await Excel.run(async (ctx) => {
      const sheets = ctx.workbook.worksheets;
      handlers.push(sheets.onChanged.add(async (e) => {
        if (e.triggerSource !== 'ThisLocalAddin') fire(e.worksheetId);
      }) as OfficeExtension.EventHandlerResult<unknown>);
      handlers.push(sheets.onRowHiddenChanged.add(async (e) => fire(e.worksheetId)) as OfficeExtension.EventHandlerResult<unknown>);
      handlers.push(sheets.onRowSorted.add(async (e) => fire(e.worksheetId)) as OfficeExtension.EventHandlerResult<unknown>);
      handlers.push(sheets.onColumnSorted.add(async (e) => fire(e.worksheetId)) as OfficeExtension.EventHandlerResult<unknown>);
      handlers.push(sheets.onAdded.add(async () => {
        await refreshNames().catch(() => undefined);
        onChange([]);
      }) as OfficeExtension.EventHandlerResult<unknown>);
      handlers.push(sheets.onDeleted.add(async () => onChange([])) as OfficeExtension.EventHandlerResult<unknown>);
      await ctx.sync();
    });
    // A format change (bold, colours, indent, a paste of formats) raises no `onChanged` and moves no fingerprint, yet
    // the index reads those styles (inputs in blue, headings in bold or indented): reported apart, so the sheet is
    // captured again whatever its fingerprint says. Its own registration: a host without it keeps the events above.
    if (onFormat && apiSupported('1.9')) {
      try {
        await Excel.run(async (ctx) => {
          const report = async (worksheetId: string): Promise<void> => {
            if (!names.has(worksheetId)) await refreshNames().catch(() => undefined);
            const name = names.get(worksheetId);
            if (name) onFormat([name]);
          };
          handlers.push(ctx.workbook.worksheets.onFormatChanged.add(async (e) => void report(e.worksheetId).catch(() => undefined)) as OfficeExtension.EventHandlerResult<unknown>);
          await ctx.sync();
        });
      } catch (err) {
        console.warn('[sculpt] workbook format events unavailable', err);
      }
    }
    return async () => {
      if (!handlers.length) return;
      await Excel.run(handlers[0].context, async (ctx) => {
        for (const h of handlers) h.remove();
        await ctx.sync();
      }).catch(() => undefined);
    };
  }

  /** Workbook name + active sheet for the welcome heading (cheap, no selection read). */
  async readWorkbookInfo(): Promise<{ name: string; activeSheet: string }> {
    return Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      wb.load('name');
      const active = wb.worksheets.getActiveWorksheet();
      active.load('name');
      await ctx.sync();
      return { name: wb.name, activeSheet: active.name };
    });
  }

  /** "+ context → Whole sheet": head of the used range of a sheet, capped at `maxCells`. */
  async readUsedRangeSnapshot(sheet: string, maxCells: number): Promise<RangeAttachment | null> {
    return Excel.run(async (ctx) => {
      const ws = sheetOf(ctx, sheet);
      const used = ws.getUsedRangeOrNullObject(true);
      used.load(['address', 'rowCount', 'columnCount']);
      await ctx.sync();
      if (used.isNullObject) return null;
      const cols = used.columnCount;
      const maxRows = Math.max(1, Math.floor(maxCells / Math.max(1, cols)));
      const truncated = used.rowCount > maxRows;
      const head = truncated ? used.getResizedRange(maxRows - used.rowCount, 0) : used;
      head.load(['address', 'values', 'formulas']);
      await ctx.sync();
      return {
        kind: 'range',
        sheet,
        address: stripSheet(head.address),
        values: head.values as unknown[][],
        formulas: (head.formulas as unknown[][]).map((r) => r.map((f) => (typeof f === 'string' && f.startsWith('=') ? f : ''))),
        truncated,
      };
    });
  }

  /** "+ context → Named ranges": every workbook-scoped and sheet-scoped name. */
  async listNamedItems(): Promise<NamedItemRef[]> {
    return Excel.run(async (ctx) => {
      const coll = ctx.workbook.names;
      coll.load('items/name,items/formula,items/scope');
      const sheets = ctx.workbook.worksheets;
      sheets.load('items/name');
      await ctx.sync();
      const sheetNames = sheets.items.map((ws) => {
        const n = ws.names;
        n.load('items/name,items/formula,items/scope');
        return n;
      });
      await ctx.sync();
      const out: NamedItemRef[] = coll.items.map((n) => ({ name: n.name, refers_to: n.formula, scope: 'workbook' }));
      for (const n of sheetNames) for (const it of n.items) out.push({ name: it.name, refers_to: it.formula, scope: 'sheet' });
      return out;
    });
  }

  /** True when a worksheet with that exact name exists (journal/undo bookkeeping). */
  async sheetExists(sheet: string): Promise<boolean> {
    if (!sheet) return false;
    return Excel.run(async (ctx) => {
      const ws = ctx.workbook.worksheets.getItemOrNullObject(sheet);
      await ctx.sync();
      return !ws.isNullObject;
    });
  }

  /** Before-image of a range for the change journal (#7). */
  async snapshotRange(sheet: string, address: string): Promise<RangeSnapshot> {
    return Excel.run(async (ctx) => {
      const range = sheetOf(ctx, sheet).getRange(stripSheet(address));
      range.load(['address', 'values', 'formulas', 'numberFormat']);
      await ctx.sync();
      return {
        sheet,
        address: stripSheet(range.address),
        values: range.values as unknown[][],
        formulas: range.formulas as unknown[][],
        numberFormats: range.numberFormat as string[][],
      };
    });
  }

  /** Restore a journal snapshot (undo). Formulas carry plain values for non-formula cells. */
  async restoreSnapshot(snap: RangeSnapshot): Promise<void> {
    await Excel.run(async (ctx) => {
      const range = sheetOf(ctx, snap.sheet).getRange(snap.address);
      range.formulas = snap.formulas as string[][];
      range.numberFormat = snap.numberFormats;
      await ctx.sync();
    });
  }

  /** Briefly select a range so the user sees what changed ("Show" action). */
  async highlight(sheet: string, address: string): Promise<void> {
    await Excel.run(async (ctx) => {
      const ws = sheetOf(ctx, sheet);
      ws.load('visibility');
      await ctx.sync();
      // A hidden (or very hidden) sheet cannot be activated, so a link to it did nothing: it is shown first. The
      // user clicked to go there; the change is theirs to undo (Ctrl+Z, or hide the sheet again).
      if (ws.visibility !== 'Visible') ws.visibility = 'Visible' as Excel.SheetVisibility;
      ws.activate();
      const a = stripSheet(address);
      // No address: the reference named the sheet alone, and activating it is the whole answer.
      if (!a.trim()) {
        await ctx.sync();
        return;
      }
      if (isMultiArea(a)) ws.getRanges(a).select();
      else ws.getRange(a).select();
      await ctx.sync();
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Read / inspect tools (R)
  // ────────────────────────────────────────────────────────────────────────

  async excel_get_workbook_overview(): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const wb = ctx.workbook;
        wb.load('name');
        const sheets = wb.worksheets;
        sheets.load('items/name,items/visibility,items/position');
        const active = wb.worksheets.getActiveWorksheet();
        active.load('name');
        const names = wb.names;
        names.load('items/name');
        const app = wb.application;
        app.load('calculationMode');
        const sel = wb.getSelectedRanges(); // multi-area safe (getSelectedRange throws on Ctrl-click selections)
        sel.load('address');
        await ctx.sync();
        const used = sheets.items.map((ws) => {
          const r = ws.getUsedRangeOrNullObject(true);
          r.load(['address', 'rowCount', 'columnCount']);
          const tables = ws.tables;
          tables.load('items/name');
          const charts = ws.charts;
          charts.load('count');
          return { r, tables, charts };
        });
        await ctx.sync();
        const fingerprints = await readFingerprints(ctx, sheets.items);
        return {
          workbook_name: wb.name,
          active_sheet: active.name,
          selection: sel.address.split(',').map((a) => stripSheet(a.trim())).join(','),
          calculation_mode: String(app.calculationMode).toLowerCase(),
          named_items_count: names.items.length,
          sheets: sheets.items.map((ws, i) => {
            const u = used[i];
            const has = !u.r.isNullObject;
            return {
              name: ws.name,
              position: ws.position,
              visibility: String(ws.visibility).toLowerCase(),
              used_range: has ? stripSheet(u.r.address) : null,
              rows: has ? u.r.rowCount : 0,
              cols: has ? u.r.columnCount : 0,
              tables: u.tables.items.map((t) => t.name),
              has_charts: u.charts.count > 0,
              fingerprint: fingerprints[i],
            };
          }),
        };
      });
    } catch (err) {
      return toToolError(err, 'excel_get_workbook_overview');
    }
  }

  async excel_get_sheet_summary(input: X.SheetSummaryInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.load(['name', 'visibility', 'position']);
        const used = ws.getUsedRangeOrNullObject(true);
        used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
        const tables = ws.tables;
        tables.load('items/name');
        const names = ws.names;
        names.load('items/name,items/formula');
        const prot = ws.protection;
        prot.load('protected');
        const freeze = ws.freezePanes.getLocationOrNullObject();
        freeze.load(['address', 'rowCount', 'columnCount']);
        // Merged areas are looked up on the WHOLE sheet: a merge of empty cells (typical title
        // band) is invisible to the values-only used range.
        const merged = ws.getRange().getMergedAreasOrNullObject();
        merged.load('areas/items/address');
        await ctx.sync();
        let firstRow: unknown[] = [];
        let firstCol: unknown[] = [];
        const hasUsed = !used.isNullObject;
        if (hasUsed) {
          const rowRange = used.getRow(0).getResizedRange(0, Math.min(used.columnCount, MAX_HEADER_CELLS) - used.columnCount);
          const colRange = used.getColumn(0).getResizedRange(Math.min(used.rowCount, MAX_HEADER_CELLS) - used.rowCount, 0);
          rowRange.load('values');
          colRange.load('values');
          await ctx.sync();
          firstRow = rowRange.values[0] as unknown[];
          firstCol = (colRange.values as unknown[][]).map((r) => r[0]);
        } else {
          await ctx.sync();
        }
        const [fingerprint] = await readFingerprints(ctx, [ws]);
        // Audit extension: hidden row / column spans and hidden formulas (gated, degrade to [] / false).
        const hidden = hasUsed ? await hiddenSpansOf(ctx, used, used.rowIndex, used.columnIndex) : { rows: [], cols: [] };
        let formulaHidden = false;
        if (hasUsed && prot.protected) {
          try {
            const sCols = Math.min(used.columnCount, FORMULA_HIDDEN_SAMPLE_CELLS);
            const sRows = Math.max(1, Math.min(used.rowCount, Math.floor(FORMULA_HIDDEN_SAMPLE_CELLS / sCols)));
            const sample = used.getResizedRange(sRows - used.rowCount, sCols - used.columnCount);
            const props = sample.getCellProperties({ format: { protection: true } });
            await ctx.sync();
            formulaHidden = props.value.some((row) => row.some((c) => !!c.format?.protection?.formulaHidden));
          } catch {
            formulaHidden = false;
          }
        }
        const out: X.SheetSummaryOutput = {
          sheet: ws.name,
          visibility: String(ws.visibility).toLowerCase(),
          position: ws.position,
          used_range: used.isNullObject ? null : stripSheet(used.address),
          rows: used.isNullObject ? 0 : used.rowCount,
          cols: used.isNullObject ? 0 : used.columnCount,
          fingerprint,
          first_row: firstRow,
          first_column: firstCol,
          tables: tables.items.map((t) => t.name),
          names: names.items.map((n) => ({ name: n.name, refers_to: n.formula })),
          merged_areas: merged.isNullObject ? [] : merged.areas.items.map((a) => stripSheet(a.address)),
          // Excel reports the frozen rectangle (top-left block); rows/cols are its dimensions.
          freeze_panes: freeze.isNullObject ? null : { address: stripSheet(freeze.address), rows: freeze.rowCount, cols: freeze.columnCount },
          protected: prot.protected,
          hidden_rows: hidden.rows,
          hidden_cols: hidden.cols,
          formula_hidden: formulaHidden,
        };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_get_sheet_summary');
    }
  }

  /** Compact first pass over a worksheet (domain #32 / D2): a window of rows is read once (values,
   *  R1C1 formulas, number formats) and reduced to one pattern per row by `scan.ts`, so cell values
   *  never cross to the model in bulk. When the window starts below the top of the sheet, the top
   *  rows are read as well so the timeline is always known. One `Excel.run`. */
  async excel_scan_sheet(input: X.ScanSheetInput): Promise<Json> {
    const rowFrom = Math.max(1, Math.floor(Number(input.row_from) || 1));
    const maxRows = Math.min(Math.max(1, Math.floor(Number(input.max_rows) || SCAN_DEFAULT_ROWS)), SCAN_MAX_ROWS);
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const used = ws.getUsedRangeOrNullObject(true);
        used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
        await ctx.sync();
        if (used.isNullObject) return emptyScan(input.sheet, rowFrom) as unknown as Json;
        const firstRow = used.rowIndex + 1;
        const lastRow = used.rowIndex + used.rowCount;
        const colIndex = used.columnIndex;
        const colCount = Math.min(used.columnCount, SCAN_MAX_COLS);
        const startRow = Math.max(rowFrom, firstRow);
        if (startRow > lastRow) {
          return { ...emptyScan(input.sheet, rowFrom), used_range: stripSheet(used.address) } as unknown as Json;
        }
        const endRow = Math.min(startRow + maxRows - 1, lastRow);
        const load = (r0: number, r1: number) => {
          const rg = ws.getRangeByIndexes(r0 - 1, colIndex, r1 - r0 + 1, colCount);
          rg.load(['values', 'formulasR1C1', 'numberFormat']);
          return rg;
        };
        const body = load(startRow, endRow);
        const head = startRow > firstRow ? load(firstRow, Math.min(firstRow + SCAN_HEAD_ROWS - 1, startRow - 1)) : null;
        await ctx.sync();
        // Row hidden flags of the window (audit extension), gated: absent flags → no `hidden` claims.
        const rowHidden = await rowHiddenFlags(ctx, body);
        const toGrid = (rg: Excel.Range, r0: number, hidden?: boolean[]): ScanGrid => ({
          startRow: r0,
          startCol: colIndex + 1,
          values: rg.values as X.CellValue[][],
          formulasR1C1: rg.formulasR1C1 as unknown[][],
          numberFormats: rg.numberFormat as string[][],
          rowHidden: hidden && hidden.length ? hidden : undefined,
        });
        const timeline = head ? detectTimeline(toGrid(head, firstRow)) : null;
        return scanGrid(input.sheet, stripSheet(used.address), toGrid(body, startRow, rowHidden), {
          rowFrom: startRow,
          lastRow,
          includeBlank: !!input.include_blank,
          timeline,
        }) as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_scan_sheet');
    }
  }

  /** Add an object to a one-column-per-object sheet (2026-09-08: the builder mirrored `company_asset` into a
   *  new sheet for a second asset; the right move is one more column). Inserts a column, copies the source
   *  column over the used rows, writes the headers and the values that differ. */
  async excel_add_object_column(input: X.AddObjectColumnInput): Promise<Json> {
    let srcIdx: number;
    let afterIdx: number;
    try {
      srcIdx = ExcelToolExecutor.rowOrColIndex(String(input.source_col).trim(), 'col');
      afterIdx = input.insert_after ? ExcelToolExecutor.rowOrColIndex(String(input.insert_after).trim(), 'col') : srcIdx;
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const headers = Object.entries(input.headers ?? {});
    if (!headers.length) return invalidInput('headers must name at least the object header cell, e.g. {"2": "Solar asset"}');
    const rowOf = (k: string): number => {
      const n = parseInt(k, 10);
      if (!Number.isFinite(n) || n < 1) throw Object.assign(new Error(`row numbers start at 1 (got ${k})`), { code: 'InvalidArgument' });
      return n;
    };
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const used = ws.getUsedRangeOrNullObject(true);
        used.load(['rowIndex', 'rowCount', 'isNullObject']);
        await ctx.sync();
        const lastRow = used.isNullObject ? Math.max(...headers.map(([k]) => rowOf(k))) : used.rowIndex + used.rowCount;
        const newIdx = afterIdx + 1;
        const newCol = colToLetter(newIdx);
        ws.getRange(`${newCol}:${newCol}`).insert('Right' as Excel.InsertShiftDirection);
        await ctx.sync();
        // The source column shifted right when it sits at or after the insertion point.
        const srcCol = colToLetter(srcIdx >= newIdx ? srcIdx + 1 : srcIdx);
        const src = ws.getRange(`${srcCol}1:${srcCol}${lastRow}`);
        const dst = ws.getRange(`${newCol}1:${newCol}${lastRow}`);
        dst.copyFrom(src, (input.copy_values === false ? 'Formats' : 'All') as Excel.RangeCopyType, false, false);
        src.load('format/columnWidth');
        await ctx.sync();
        dst.format.columnWidth = src.format.columnWidth;
        for (const r of input.clear_rows ?? []) ws.getRange(`${newCol}${rowOf(String(r))}`).clear('Contents' as Excel.ClearApplyTo);
        for (const [k, v] of headers) ws.getRange(`${newCol}${rowOf(k)}`).values = [[asText(v ?? '')]];
        for (const [k, v] of Object.entries(input.values ?? {})) ws.getRange(`${newCol}${rowOf(k)}`).values = [[asText(v ?? '')]];
        await ctx.sync();
        const out: X.AddObjectColumnOutput = { status: 'ok', col: newCol, rows: lastRow, _changes: changes(input.sheet, `${newCol}1:${newCol}${lastRow}`) };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_add_object_column');
    }
  }

  /** Structural anchors of a platform-generated workbook (M3 row f): the veryHidden `_pcp_sync` sheet
   *  carries a JSON manifest (chunked in column A) naming, per generated sheet, the row (`cols` axis)
   *  or column (`rows` axis) that holds the object ids. Reads that line of every listed sheet, bounded
   *  by the sheet's used range, and returns the simulator's shape; `present: false` without a manifest,
   *  `InvalidManifest` when the JSON is unreadable. One `Excel.run`. */
  async excel_read_sync_anchors(): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const manifestSheet = ctx.workbook.worksheets.getItemOrNullObject(PCP_SYNC_SHEET);
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        if (manifestSheet.isNullObject) return { present: false, anchors: {} };
        const column = manifestSheet.getRange(`A1:A${MANIFEST_MAX_CHUNK_ROWS}`);
        column.load('values');
        await ctx.sync();
        let lines: AnchorLine[];
        let manifest;
        try {
          manifest = parseManifestText(manifestTextFromColumn(column.values as unknown[][]));
          lines = anchorLines(manifest);
        } catch (err) {
          const message = err instanceof InvalidManifestError ? err.message : `The _pcp_sync manifest could not be parsed: ${err instanceof Error ? err.message : String(err)}`;
          return { present: true, anchors: {}, error: message, code: 'InvalidManifest', retryable: false, hint: 'Treat the workbook as not generated by the platform: discover its structure with excel_scan_sheet instead.' };
        }
        const existing = new Set(sheets.items.map((ws) => ws.name));
        // Used-range bounds of every anchored sheet (one sync), then the anchor lines themselves (one sync).
        const targets = lines
          .filter((line) => existing.has(line.sheet))
          .map((line) => {
            const ws = ctx.workbook.worksheets.getItem(line.sheet);
            const used = ws.getUsedRangeOrNullObject(true);
            used.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
            return { line, ws, used };
          });
        await ctx.sync();
        const reads = targets.map(({ line, ws, used }) => {
          if (used.isNullObject) return { line, range: null, first: 1 };
          if (line.axis === 'cols') {
            // The anchor row across the used columns.
            const range = ws.getRangeByIndexes(line.index - 1, used.columnIndex, 1, used.columnCount);
            range.load('values');
            return { line, range, first: used.columnIndex + 1 };
          }
          // The anchor column down the used rows; empty cells do not count, so read a bounded span.
          const rows = Math.min(used.rowCount, ANCHOR_MAX_OBJECTS * 10);
          const range = ws.getRangeByIndexes(used.rowIndex, line.index - 1, rows, 1);
          range.load('values');
          return { line, range, first: used.rowIndex + 1 };
        });
        await ctx.sync();
        const anchors: Record<string, X.SyncAnchor> = {};
        for (const { line, range, first } of reads) {
          const grid = range ? (range.values as unknown[][]) : [];
          const cells = line.axis === 'cols' ? (grid[0] ?? []) : grid.map((r) => r[0]);
          anchors[line.sheet] = anchorObjects(line, cells, first);
        }
        return anchorsOf(manifest, anchors) as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_read_sync_anchors');
    }
  }

  /** Read a range, paginated like the eval simulator (M3 row e): over `max_cells` the first rows that
   *  fit are returned with `truncated`, `next_row` and `requested_address`; only a single row wider
   *  than `max_cells` is a ResultTooLarge error. Pure window arithmetic in `readPage.ts`. */
  async excel_read_range(input: X.ReadRangeInput): Promise<Json> {
    const maxCells = clampMaxCells(input.max_cells);
    const page = readPage(input.address ?? '', maxCells);
    if (!page.ok) return page.reason === 'invalid' ? invalidInput(page.message) : tooLarge(page.cells, page.maxCells);
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(page.address);
        const props = ['address', 'rowCount', 'columnCount', 'values', 'valueTypes'];
        if (input.include_formulas !== false) props.push('formulas');
        if (input.include_number_formats) props.push('numberFormat');
        range.load(props);
        await ctx.sync();
        const out: X.ReadRangeOutput = {
          sheet: input.sheet,
          address: stripSheet(range.address),
          rows: range.rowCount,
          cols: range.columnCount,
          values: range.values as X.CellValue[][],
          value_types: summariseTypes(range.valueTypes as unknown as string[][]),
          truncated: page.truncated,
        };
        if (page.truncated) {
          out.requested_address = page.requested;
          out.next_row = page.nextRow as number;
        }
        if (input.include_formulas !== false) {
          // Only keep real formulas; plain values are already in `values` (keeps results compact).
          out.formulas = (range.formulas as unknown[][]).map((r) => r.map((f) => (typeof f === 'string' && f.startsWith('=') ? f : '')));
        }
        if (input.include_number_formats) out.number_formats = range.numberFormat as string[][];
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_read_range');
    }
  }

  async excel_get_selection(): Promise<Json> {
    try {
      const sel = await Excel.run(async (ctx) => {
        const range = ctx.workbook.getSelectedRange();
        const ws = range.worksheet;
        range.load(['address', 'rowCount', 'columnCount']);
        ws.load('name');
        await ctx.sync();
        return { sheet: ws.name, address: stripSheet(range.address), cells: range.rowCount * range.columnCount };
      });
      if (sel.cells > DEFAULT_MAX_CELLS) {
        return { sheet: sel.sheet, address: sel.address, cells: sel.cells, note: 'Selection too large to inline; use excel_read_range on a sub-range.' };
      }
      return this.excel_read_range({ sheet: sel.sheet, address: sel.address, include_formulas: true });
    } catch (err) {
      const multi = await this.readSelectionAddress();
      if (multi && multi.address.includes(',')) {
        return { sheet: multi.sheet, address: multi.address, areas: multi.address.split(','), note: 'Multi-area selection: read each area with excel_read_range.' };
      }
      return toToolError(err, 'excel_get_selection');
    }
  }

  async excel_find_cells(input: X.FindCellsInput): Promise<Json> {
    if (!input.query) return invalidInput('query is required');
    const max = Math.min(Math.max(1, input.max_results ?? MAX_FIND_RESULTS), MAX_FIND_RESULTS);
    try {
      return await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        const targets = input.sheet ? [sheetOf(ctx, input.sheet)] : sheets.items;
        const hits: { sheet: string; address: string; value: unknown; formula: unknown }[] = [];
        let truncated = false;
        if ((input.look_in ?? 'values') === 'values') {
          const found = targets.map((ws) => {
            ws.load('name');
            const areas = ws.findAllOrNullObject(input.query, { completeMatch: !!input.whole_cell, matchCase: !!input.match_case });
            areas.load('areas/items/address');
            return { ws, areas };
          });
          await ctx.sync();
          const cells: { ws: Excel.Worksheet; r: Excel.Range }[] = [];
          for (const f of found) {
            if (f.areas.isNullObject) continue;
            for (const area of f.areas.areas.items) {
              const shape = rangeShape(stripSheet(area.address));
              // findAll coalesces adjacent hits into areas; expand to cells (capped).
              for (let r = 0; r < shape.rows; r++) {
                for (let c = 0; c < shape.cols; c++) {
                  if (cells.length >= max) { truncated = true; break; }
                  cells.push({ ws: f.ws, r: f.ws.getRange(stripSheet(area.address)).getCell(r, c) });
                }
              }
            }
          }
          for (const c of cells) c.r.load(['address', 'values', 'formulas']);
          await ctx.sync();
          for (const c of cells) hits.push({ sheet: c.ws.name, address: stripSheet(c.r.address), value: c.r.values[0][0], formula: c.r.formulas[0][0] });
        } else {
          // Office.js findAll searches values only → scan formulas client-side (used ranges, capped).
          const used = targets.map((ws) => {
            ws.load('name');
            const u = ws.getUsedRangeOrNullObject(false);
            u.load(['address', 'formulas', 'values', 'rowCount', 'columnCount']);
            return { ws, u };
          });
          await ctx.sync();
          const q = input.match_case ? input.query : input.query.toLowerCase();
          outer: for (const { ws, u } of used) {
            if (u.isNullObject) continue;
            const start = parseRange(stripSheet(u.address)).start;
            const formulas = u.formulas as unknown[][];
            for (let r = 0; r < u.rowCount; r++) {
              for (let c = 0; c < u.columnCount; c++) {
                const f = formulas[r][c];
                if (typeof f !== 'string' || !f.startsWith('=')) continue;
                const hay = input.match_case ? f : f.toLowerCase();
                const ok = input.whole_cell ? hay === q : hay.includes(q);
                if (!ok) continue;
                if (hits.length >= max) { truncated = true; break outer; }
                hits.push({ sheet: ws.name, address: `${colToLetter(start.col + c)}${start.row + r}`, value: (u.values as unknown[][])[r][c], formula: f });
              }
            }
          }
        }
        return { query: input.query, look_in: input.look_in ?? 'values', count: hits.length, truncated, hits };
      });
    } catch (err) {
      return toToolError(err, 'excel_find_cells');
    }
  }

  async excel_get_named_items(input: X.NamedItemsInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const coll = input.sheet ? sheetOf(ctx, input.sheet).names : ctx.workbook.names;
        coll.load('items/name,items/formula,items/type,items/value,items/comment,items/visible,items/scope');
        await ctx.sync();
        return {
          scope: input.sheet ?? 'workbook',
          count: coll.items.length,
          names: coll.items.map((n) => ({
            name: n.name,
            refers_to: n.formula,
            scope: String(n.scope) === 'Worksheet' ? 'sheet' : 'workbook',
            type: String(n.type),
            value: n.value,
            comment: n.comment,
            visible: n.visible,
          })),
        };
      });
    } catch (err) {
      return toToolError(err, 'excel_get_named_items');
    }
  }

  async excel_list_tables(input: X.ListTablesInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const coll = input.sheet ? sheetOf(ctx, input.sheet).tables : ctx.workbook.tables;
        coll.load('items/name,items/showHeaders,items/showTotals,items/style');
        await ctx.sync();
        const ranges = coll.items.map((t) => {
          const r = t.getRange();
          r.load(['address', 'rowCount']);
          const ws = t.worksheet;
          ws.load('name');
          return { r, ws };
        });
        await ctx.sync();
        return {
          count: coll.items.length,
          tables: coll.items.map((t, i) => ({ name: t.name, sheet: ranges[i].ws.name, address: stripSheet(ranges[i].r.address), rows: ranges[i].r.rowCount - (t.showHeaders ? 1 : 0), has_headers: t.showHeaders, has_totals: t.showTotals, style: t.style })),
        };
      });
    } catch (err) {
      return toToolError(err, 'excel_list_tables');
    }
  }

  async excel_get_table(input: X.GetTableInput): Promise<Json> {
    if (!input.table_name) return invalidInput('table_name is required');
    const sample = Math.min(Math.max(0, input.sample_rows ?? 5), 50);
    try {
      return await Excel.run(async (ctx) => {
        const t = ctx.workbook.tables.getItem(input.table_name);
        t.load(['name', 'showHeaders', 'showTotals', 'style']);
        const ws = t.worksheet;
        ws.load('name');
        const range = t.getRange();
        range.load(['address', 'rowCount']);
        const header = t.getHeaderRowRange();
        header.load('values');
        const body = t.getDataBodyRange();
        body.load('rowCount');
        await ctx.sync();
        let sampleRows: unknown[][] = [];
        if (sample > 0 && body.rowCount > 0) {
          const s = body.getResizedRange(Math.min(sample, body.rowCount) - body.rowCount, 0);
          s.load('values');
          await ctx.sync();
          sampleRows = s.values as unknown[][];
        }
        let totals: unknown[] | null = null;
        if (t.showTotals) {
          const tr = t.getTotalRowRange();
          tr.load('values');
          await ctx.sync();
          totals = tr.values[0] as unknown[];
        }
        return { name: t.name, sheet: ws.name, address: stripSheet(range.address), headers: header.values[0], row_count: body.rowCount, sample: sampleRows, totals_row: totals, style: t.style };
      });
    } catch (err) {
      return toToolError(err, 'excel_get_table');
    }
  }

  async excel_get_precedents(input: X.PrecedentsInput): Promise<Json> {
    return this.precedentsOrDependents(input, 'precedents');
  }

  async excel_get_dependents(input: X.PrecedentsInput): Promise<Json> {
    return this.precedentsOrDependents(input, 'dependents');
  }

  async excel_get_precedents_batch(input: X.PrecedentsBatchInput): Promise<Json> {
    return this.traceBatch(input, 'precedents');
  }

  async excel_get_dependents_batch(input: X.PrecedentsBatchInput): Promise<Json> {
    return this.traceBatch(input, 'dependents');
  }

  /** Batched trace (PLAN §13 D6). Sync strategy: (1) one run loading every cell's formula/value,
   *  (2) one run queuing the trace of every eligible cell (precedents need a formula; dependents
   *  are queued for all) and a single sync; if Office rejects the batch (one bad item fails the
   *  whole sync), (3) the failed cells are retried one by one so a single bad cell never hides the
   *  others. Per-cell failures land in the entry's `error`. */
  private async traceBatch(input: X.PrecedentsBatchInput, kind: TraceKind): Promise<Json> {
    const resolved = resolveBatchCells(input.sheet, input.cells);
    if (resolved.error) return invalidInput(resolved.error);
    const levels = batchLevels(input.levels);
    const maxPer = Number(input.max_addresses_per_cell) > 0 ? Number(input.max_addresses_per_cell) : DEFAULT_MAX_ADDRESSES_PER_CELL;
    const refs = resolved.refs;
    const results: BatchCellResult[] = refs.map((r) => failedEntry(r, r.error ?? 'pending'));
    const valid = refs.map((r, i) => ({ r, i })).filter(({ r }) => !r.error);
    try {
      // (1) formulas / values of every valid cell in one sync.
      const meta = await Excel.run(async (ctx) => {
        const ranges = valid.map(({ r }) => {
          const rg = sheetOf(ctx, r.sheet).getRange(r.cell);
          rg.load(['formulas', 'values']);
          return rg;
        });
        await ctx.sync();
        return ranges.map((rg) => ({ formula: String(rg.formulas[0][0] ?? ''), value: rg.values[0][0] as unknown }));
      });
      const eligible: { r: BatchCellRef; i: number; formula: string; value: unknown }[] = [];
      valid.forEach(({ r, i }, k) => {
        const { formula, value } = meta[k];
        const hasFormula = formula.startsWith('=');
        if (kind === 'precedents' && !hasFormula) {
          results[i] = { ...failedEntry(r, 'no formula in this cell'), formula: null, value };
        } else {
          eligible.push({ r, i, formula: hasFormula ? formula : '', value });
        }
      });
      // (2) one batched trace; (3) per-cell fallback for whatever Office rejected.
      const done = await this.traceMany(eligible, kind, levels, maxPer, results);
      if (!done) {
        for (const e of eligible) {
          if (results[e.i].error === 'pending') await this.traceMany([e], kind, levels, maxPer, results, true);
        }
      }
    } catch (err) {
      return toToolError(err, `excel_get_${kind}_batch`);
    }
    return shapeBatch(kind, levels, results, refs.length) as unknown as Json;
  }

  /** Queue the trace of every entry and sync once. Returns false when the batch sync failed (nothing
   *  written to `results`); with `single`, a failure is recorded on that entry instead. */
  private async traceMany(
    entries: { r: BatchCellRef; i: number; formula: string; value: unknown }[],
    kind: TraceKind,
    levels: 1 | 'all',
    maxPer: number,
    results: BatchCellResult[],
    single = false,
  ): Promise<boolean> {
    if (!entries.length) return true;
    try {
      await Excel.run(async (ctx) => {
        const areas = entries.map(({ r }) => {
          const cell = sheetOf(ctx, r.sheet).getRange(r.cell);
          const a = kind === 'precedents' ? (levels === 1 ? cell.getDirectPrecedents() : cell.getPrecedents()) : levels === 1 ? cell.getDirectDependents() : cell.getDependents();
          a.load('addresses');
          return a;
        });
        await ctx.sync();
        entries.forEach((e, k) => {
          const all = areas[k].addresses ?? [];
          const { addresses, truncated } = capAddresses(all, maxPer);
          results[e.i] = { sheet: e.r.sheet, cell: e.r.cell, formula: e.formula || null, value: e.value, count: all.length, truncated, addresses };
        });
      });
      return true;
    } catch (err) {
      if (!single) return false;
      const e = toToolError(err, `excel_get_${kind}`);
      const entry = entries[0];
      const none = e.code === 'ItemNotFound' || e.code === 'InvalidArgument';
      results[entry.i] = none
        ? { sheet: entry.r.sheet, cell: entry.r.cell, formula: entry.formula || null, value: entry.value, count: 0, truncated: false, addresses: [] }
        : { ...failedEntry(entry.r, String(e.error)), formula: entry.formula || null, value: entry.value };
      return true;
    }
  }

  private async precedentsOrDependents(input: X.PrecedentsInput, kind: 'precedents' | 'dependents'): Promise<Json> {
    // Sheet-qualified `cell` ('Inputs!C9') overrides `sheet`, like the batch tools.
    const split = splitSheetPrefix(input.cell);
    const sheet = split.sheet ?? input.sheet;
    const cellA1 = stripSheet(split.address);
    try {
      parseCell(cellA1);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const levels = Math.min(Math.max(1, input.levels ?? 1), 10);
    try {
      return await Excel.run(async (ctx) => {
        const cell = sheetOf(ctx, sheet).getRange(cellA1);
        cell.load(['formulas', 'values']);
        const direct = levels === 1;
        const areas = kind === 'precedents' ? (direct ? cell.getDirectPrecedents() : cell.getPrecedents()) : direct ? cell.getDirectDependents() : cell.getDependents();
        areas.load('addresses');
        await ctx.sync();
        const addresses = areas.addresses.slice(0, MAX_PRECEDENT_ADDRESSES);
        return { sheet, cell: cellA1, formula: cell.formulas[0][0], value: cell.values[0][0], kind, levels: direct ? 1 : 'all', count: areas.addresses.length, truncated: areas.addresses.length > addresses.length, addresses };
      });
    } catch (err) {
      const e = toToolError(err, `excel_get_${kind}`);
      if (e.code === 'ItemNotFound' || e.code === 'InvalidArgument') {
        return { sheet, cell: cellA1, kind, count: 0, addresses: [], note: `No ${kind} found (or the cell holds no formula).` };
      }
      return e;
    }
  }

  async excel_scan_errors(input: X.ScanErrorsInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        const targets = input.sheet ? [sheetOf(ctx, input.sheet)] : sheets.items;
        const used = targets.map((ws) => {
          ws.load('name');
          const u = ws.getUsedRangeOrNullObject(true);
          u.load(['address', 'valueTypes', 'values', 'formulas', 'rowCount', 'columnCount']);
          return { ws, u };
        });
        await ctx.sync();
        const errors: { sheet: string; address: string; error: unknown; formula: unknown }[] = [];
        const byKind: Record<string, number> = {};
        let truncated = false;
        outer: for (const { ws, u } of used) {
          if (u.isNullObject) continue;
          const start = parseRange(stripSheet(u.address)).start;
          const types = u.valueTypes as unknown as string[][];
          for (let r = 0; r < u.rowCount; r++) {
            for (let c = 0; c < u.columnCount; c++) {
              if (types[r][c] !== 'Error') continue;
              const v = (u.values as unknown[][])[r][c];
              byKind[String(v)] = (byKind[String(v)] ?? 0) + 1;
              if (errors.length >= MAX_ERROR_CELLS) { truncated = true; continue outer; }
              errors.push({ sheet: ws.name, address: `${colToLetter(start.col + c)}${start.row + r}`, error: v, formula: (u.formulas as unknown[][])[r][c] });
            }
          }
        }
        return { scope: input.sheet ?? 'workbook', count: errors.length, by_kind: byKind, truncated, errors };
      });
    } catch (err) {
      return toToolError(err, 'excel_scan_errors');
    }
  }

  async excel_get_range_format(input: X.RangeFormatInput): Promise<Json> {
    const areas = areasOrError(input.address);
    if (!Array.isArray(areas)) return areas;
    const n = areasCellCount(areas);
    if (n > DEFAULT_MAX_CELLS) return tooLarge(n, DEFAULT_MAX_CELLS);
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const loaded = areas.map((address) => {
          const range = ws.getRange(address);
          range.load(['address', 'numberFormat']);
          const props = range.getCellProperties({
            format: {
              font: { bold: true, italic: true, size: true, name: true, color: true },
              fill: { color: true },
              horizontalAlignment: true,
              verticalAlignment: true,
              wrapText: true,
              indentLevel: true,
              borders: { style: true, color: true },
            },
          });
          const cols = range.getColumnProperties({ format: { columnWidth: true } });
          const rows = range.getRowProperties({ format: { rowHeight: true } });
          return { address, range, props, cols, rows };
        });
        await ctx.sync();
        const perArea = loaded.map(({ address, range, props, cols, rows }) => {
          // Compress per-cell formats into runs of identical formats (row-major) to stay compact.
          const runs: { from: string; to: string; format: unknown }[] = [];
          const shape = rangeShape(address);
          const start = parseRange(address).start;
          let prevKey = '';
          for (let r = 0; r < shape.rows; r++) {
            for (let c = 0; c < shape.cols; c++) {
              const cellFmt = props.value[r][c].format ?? {};
              const nf = (range.numberFormat as string[][])[r][c];
              const fmt = { ...cellFmt, numberFormat: nf };
              const key = JSON.stringify(fmt);
              const addr = `${colToLetter(start.col + c)}${start.row + r}`;
              if (key === prevKey && runs.length) runs[runs.length - 1].to = addr;
              else runs.push({ from: addr, to: addr, format: fmt });
              prevKey = key;
            }
          }
          return {
            address: stripSheet(range.address),
            runs,
            column_widths: cols.value.map((c) => c.format?.columnWidth),
            row_heights: rows.value.map((r) => r.format?.rowHeight),
          };
        });
        if (perArea.length === 1) return { sheet: input.sheet, ...perArea[0] };
        return { sheet: input.sheet, address: areas.join(','), areas: perArea };
      });
    } catch (err) {
      return toToolError(err, 'excel_get_range_format');
    }
  }

  async excel_list_charts(input: X.ListChartsInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        const targets = input.sheet ? [sheetOf(ctx, input.sheet)] : sheets.items;
        const colls = targets.map((ws) => {
          ws.load('name');
          const charts = ws.charts;
          charts.load('items/name,items/chartType,items/top,items/left,items/width,items/height');
          return { ws, charts };
        });
        await ctx.sync();
        const details = colls.flatMap(({ ws, charts }) => charts.items.map((ch) => {
          const title = ch.title;
          title.load(['text', 'visible']);
          const series = ch.series;
          series.load('items/name,items/chartType,items/axisGroup,items/hasDataLabels');
          const legend = ch.legend;
          legend.load(['visible', 'position']);
          return { ws, ch, title, series, legend };
        }));
        await ctx.sync();
        const out: Record<string, unknown>[] = [];
        for (const { ws, ch, title, series, legend } of details) {
          const entry: Record<string, unknown> = {
            sheet: ws.name,
            name: ch.name,
            type: String(ch.chartType),
            title: title.visible ? title.text : null,
            series: series.items.map((s) => ({ name: s.name, type: String(s.chartType), axis_group: String(s.axisGroup), data_labels: s.hasDataLabels })),
            legend_position: legend.visible ? String(legend.position).toLowerCase() : 'none',
            position: { top: ch.top, left: ch.left, width: ch.width, height: ch.height },
          };
          // Axes do not exist on pie/doughnut charts: read them in a guarded pass per chart.
          try {
            const value = ch.axes.valueAxis;
            const category = ch.axes.categoryAxis;
            value.load(['numberFormat']);
            value.title.load(['text', 'visible']);
            value.majorGridlines.load('visible');
            category.title.load(['text', 'visible']);
            await ctx.sync();
            entry.axis_titles = { category: category.title.visible ? category.title.text : null, value: value.title.visible ? value.title.text : null };
            entry.value_number_format = value.numberFormat;
            entry.gridlines = value.majorGridlines.visible;
          } catch {
            entry.axis_titles = null;
          }
          out.push(entry);
        }
        return { count: out.length, charts: out };
      });
    } catch (err) {
      return toToolError(err, 'excel_list_charts');
    }
  }

  async excel_get_comments(input: X.GetCommentsInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        const targets = input.sheet ? [sheetOf(ctx, input.sheet)] : sheets.items;
        const colls = targets.map((ws) => {
          ws.load('name');
          const comments = ws.comments;
          comments.load('items/content,items/authorName,items/resolved');
          return { ws, comments };
        });
        await ctx.sync();
        const locs = colls.flatMap(({ ws, comments }) => comments.items.map((c) => {
          const loc = c.getLocation();
          loc.load('address');
          return { ws, c, loc };
        }));
        await ctx.sync();
        return { count: locs.length, comments: locs.map(({ ws, c, loc }) => ({ sheet: ws.name, address: stripSheet(loc.address), author: c.authorName, text: c.content, resolved: c.resolved })) };
      });
    } catch (err) {
      return toToolError(err, 'excel_get_comments');
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // Audit tools (AUDIT_SPEC §2.1)
  // ────────────────────────────────────────────────────────────────────────

  /** One page of a sheet capture (server-bound, §2.4): values, A1 + R1C1 formulas and number formats of
   *  rows `rowFrom..rowFrom+maxRows-1` over the full column span of the used range, row hidden flags
   *  (gated), plus — with `withMeta` — the sheet facts of chunk 0. The ToolRunner loops over the pages,
   *  chunks and uploads them. One `Excel.run`; never throws. */
  async readSheetCapture(sheet: string, rowFrom: number, maxRows: number, withMeta = true): Promise<A.CapturePage | X.ToolError> {
    const from = Math.max(1, Math.floor(Number(rowFrom) || 1));
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, sheet);
        ws.load('visibility');
        const prot = ws.protection;
        prot.load('protected');
        const used = ws.getUsedRangeOrNullObject(true);
        used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
        const merged = ws.getRange().getMergedAreasOrNullObject();
        merged.load('areas/items/address');
        await ctx.sync();
        const mergedAreas = merged.isNullObject ? [] : merged.areas.items.map((a) => stripSheet(a.address));
        const base = { merged_areas: mergedAreas, visibility: String(ws.visibility).toLowerCase(), protected: prot.protected, captured_at: new Date().toISOString() };
        if (used.isNullObject) {
          const meta: A.CaptureMeta = { used_range: null, row_from: from, row_to: from - 1, col_from: 'A', col_to: 'A', rows: 0, cols: 0, hidden_rows: [], hidden_cols: [], ...base };
          return { meta: withMeta ? meta : null, rows: [], row_to: from - 1, last_row: 0 };
        }
        const firstRow = used.rowIndex + 1;
        const lastRow = used.rowIndex + used.rowCount;
        const colIndex = used.columnIndex;
        const colCount = used.columnCount;
        const startRow = Math.max(from, firstRow);
        let meta: A.CaptureMeta | null = null;
        if (withMeta) {
          const hidden = await hiddenSpansOf(ctx, used, used.rowIndex, colIndex);
          meta = {
            used_range: stripSheet(used.address),
            row_from: startRow,
            row_to: lastRow,
            col_from: colToLetter(colIndex + 1),
            col_to: colToLetter(colIndex + colCount),
            rows: used.rowCount,
            cols: colCount,
            hidden_rows: hidden.rows,
            hidden_cols: hidden.cols,
            ...base,
          };
        }
        if (startRow > lastRow) return { meta, rows: [], row_to: startRow - 1, last_row: lastRow };
        const pageRows = Math.max(1, Math.min(Math.floor(Number(maxRows) || 1), Math.floor(CAPTURE_MAX_CELLS_PER_PAGE / Math.max(1, colCount))));
        const endRow = Math.min(startRow + pageRows - 1, lastRow);
        const rg = ws.getRangeByIndexes(startRow - 1, colIndex, endRow - startRow + 1, colCount);
        rg.load(['values', 'formulas', 'formulasR1C1', 'numberFormat']);
        await ctx.sync();
        const hidden = await rowHiddenFlags(ctx, rg);
        const values = rg.values as X.CellValue[][];
        const formulas = rg.formulas as unknown[][];
        const r1c1 = rg.formulasR1C1 as unknown[][];
        const formats = rg.numberFormat as string[][];
        const rows: A.CaptureRow[] = values.map((v, i) => ({ n: startRow + i, hidden: !!hidden[i], values: v, formulas: formulas[i], r1c1: r1c1[i], formats: formats[i] }));
        return { meta, rows, row_to: endRow, last_row: lastRow };
      });
    } catch (err) {
      return toToolError(err, 'excel_capture_sheet');
    }
  }

  // ---- Workbook index (pane open, 2026-09-14) ------------------------------------------------
  /** The sheets of the workbook, in tab order, for the index job. */
  async listSheetsForIndex(): Promise<{ workbookName: string; sheets: IndexSheetListing[] }> {
    return Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      wb.load('name');
      const sheets = wb.worksheets;
      sheets.load('items/name,items/visibility,items/position');
      await ctx.sync();
      return { workbookName: wb.name, sheets: sheets.items.map((ws) => ({ name: ws.name, position: ws.position, visibility: String(ws.visibility).toLowerCase() })) };
    });
  }

  /** Size of the workbook without reading a single cell: sheet count and the cells their used ranges cover.
   *  Two syncs, so it is cheap enough to run before asking the user whether to index at all — which is what
   *  it is for (`estimateIndexSeconds`). */
  async measureWorkbook(): Promise<{ sheets: number; cells: number }> {
    return Excel.run(async (ctx) => {
      const sheets = ctx.workbook.worksheets;
      sheets.load('items/name');
      await ctx.sync();
      const used = sheets.items.map((ws) => {
        const r = ws.getUsedRangeOrNullObject(true);
        r.load(['rowCount', 'columnCount']);
        return r;
      });
      await ctx.sync();
      const cells = used.reduce((n, r) => n + (r.isNullObject ? 0 : r.rowCount * r.columnCount), 0);
      return { sheets: sheets.items.length, cells };
    });
  }

  /** Defined names → the reference they point to (the leading "=" removed), for the index job. */
  /** Excel's iterative calculation setting for the index job (ExcelApi 1.9); null when the host cannot read it. */
  async readIterativeCalculation(): Promise<{ enabled: boolean; max_iterations: number; max_change: number } | null> {
    if (!apiSupported('1.9')) return null;
    try {
      return await Excel.run(async (ctx) => {
        const it = ctx.workbook.application.iterativeCalculation;
        it.load(['enabled', 'maxIteration', 'maxChange']);
        await ctx.sync();
        return { enabled: !!it.enabled, max_iterations: Number(it.maxIteration), max_change: Number(it.maxChange) };
      });
    } catch {
      return null;
    }
  }

  async listDefinedNames(): Promise<Record<string, string>> {
    const out: Record<string, string> = {};
    for (const n of await this.listNamedItems()) {
      if (n.refers_to) out[n.name] = n.refers_to.replace(/^=/, '');
    }
    return out;
  }

  /** One whole sheet for the index: values, A1 and R1C1 formulas and number formats of the used range in one
   *  load (row bands above INDEX_BAND_CELLS), the label-area styles, tables, merged areas, hidden spans, data
   *  validation rules, the fingerprint the per-turn context will be compared with. */
  async readSheetForIndex(sheet: string, position: number): Promise<IndexSheetPayload> {
    return Excel.run(async (ctx) => {
      const ws = sheetOf(ctx, sheet);
      ws.load('visibility');
      const used = ws.getUsedRangeOrNullObject(true);
      used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
      const tables = ws.tables;
      tables.load('items/name');
      const charts = ws.charts;
      charts.load('count');
      await ctx.sync();
      // Merged areas of the whole sheet: gated and guarded (a failure costs the merged list, not the sheet).
      let mergedAreas: string[] = [];
      if (apiSupported('1.7')) {
        try {
          const merged = ws.getRange().getMergedAreasOrNullObject();
          merged.load('areas/items/address');
          await ctx.sync();
          mergedAreas = merged.isNullObject ? [] : merged.areas.items.map((a) => stripSheet(a.address));
        } catch {
          mergedAreas = [];
        }
      }
      const tableRanges = tables.items.map((t) => {
        const r = t.getRange();
        r.load('address');
        return r;
      });
      const base = {
        name: sheet,
        position,
        visibility: String(ws.visibility).toLowerCase(),
        charts: charts.count,
        merged: mergedAreas,
      };
      if (used.isNullObject) {
        // The fingerprint the turn context will be compared with, read by the same function (an empty sheet
        // included: uploading null left the server nothing to match, so it was re-uploaded at every open).
        const fingerprint = await readFingerprint(ctx, ws);
        return { ...base, used_range: null, fingerprint, tables: tableRanges.map((r, i) => ({ name: tables.items[i].name, ref: stripSheet(r.address) })), hidden_rows: [], hidden_cols: [], validations: [], cells: [] };
      }
      const rowIndex = used.rowIndex;
      const colIndex = used.columnIndex;
      const rows = used.rowCount;
      const cols = used.columnCount;
      const hidden = await hiddenSpansOf(ctx, used, rowIndex, colIndex);
      const validations = await validationsOf(ctx, ws, used);
      const bandRows = Math.max(1, Math.floor(INDEX_BAND_CELLS / Math.max(1, cols)));
      const styleCols = Math.min(cols, INDEX_STYLE_COLS);
      const cells: IndexCell[] = [];
      const formulaRows: unknown[][] = [];
      for (let r0 = 0; r0 < rows; r0 += bandRows) {
        const n = Math.min(bandRows, rows - r0);
        const rg = ws.getRangeByIndexes(rowIndex + r0, colIndex, n, cols);
        rg.load(['values', 'formulas', 'formulasR1C1', 'numberFormat']);
        const props = apiSupported('1.9') ? ws.getRangeByIndexes(rowIndex + r0, colIndex, n, styleCols).getCellProperties({ format: { font: { bold: true, color: true }, fill: { color: true }, indentLevel: true } }) : null;
        await ctx.sync();
        let styles: GridStyles | undefined;
        if (props) {
          try {
            const v = props.value;
            styles = {
              bold: v.map((row) => row.map((c) => !!c.format?.font?.bold)),
              indent: v.map((row) => row.map((c) => Number(c.format?.indentLevel) || 0)),
              fontColor: v.map((row) => row.map((c) => c.format?.font?.color ?? null)),
              fill: v.map((row) => row.map((c) => c.format?.fill?.color ?? null)),
            };
          } catch {
            styles = undefined;
          }
        }
        cells.push(...encodeCells(rg.values as unknown[][], rg.formulas as unknown[][], rg.formulasR1C1 as unknown[][], rg.numberFormat as string[][], rowIndex + r0, colIndex, styles));
        for (const row of rg.formulas as unknown[][]) formulaRows.push(row);
      }
      // The fingerprint the turn context will be compared with: `readFingerprint`'s parts, from the formulas this
      // capture has just loaded. Reading them a second time for it cost 0.6 s on a 344k-cell sheet.
      const fingerprint = fingerprintSheet({
        usedRange: stripSheet(used.address),
        cellCount: rows * cols,
        formulas: formulaRows,
        hiddenRows: hidden.rows,
        hiddenCols: hidden.cols,
        tableCount: tables.items.length,
        chartCount: charts.count,
      });
      return {
        ...base,
        used_range: stripSheet(used.address),
        fingerprint,
        tables: tableRanges.map((r, i) => ({ name: tables.items[i].name, ref: stripSheet(r.address) })),
        hidden_rows: expandSpans(hidden.rows),
        hidden_cols: expandSpans(hidden.cols),
        validations,
        cells,
      };
    });
  }

  /** The values alone of a sheet captured again only because it reads one that changed (`IndexSheetValues`): its
   *  formulas, formats and styles are the previous version's, under the same `fingerprint`. The styles are most of a
   *  full capture's time (`getCellProperties`: 2.5 of 3.4 s on a 344k-cell sheet, its values 0.05 s). */
  async readSheetValuesForIndex(sheet: string, position: number, fingerprint: string | null): Promise<IndexSheetValues> {
    return Excel.run(async (ctx) => {
      const ws = sheetOf(ctx, sheet);
      ws.load('visibility');
      const used = ws.getUsedRangeOrNullObject(true);
      used.load(['rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
      await ctx.sync();
      const cells: [number, number, unknown][] = [];
      if (!used.isNullObject) {
        const { rowIndex, columnIndex: colIndex, rowCount: rows, columnCount: cols } = used;
        const bandRows = Math.max(1, Math.floor(INDEX_BAND_CELLS / Math.max(1, cols)));
        for (let r0 = 0; r0 < rows; r0 += bandRows) {
          const rg = ws.getRangeByIndexes(rowIndex + r0, colIndex, Math.min(bandRows, rows - r0), cols);
          rg.load('values');
          await ctx.sync();
          cells.push(...encodeValues(rg.values as unknown[][], rowIndex + r0, colIndex));
        }
      }
      return { name: sheet, position, visibility: String(ws.visibility).toLowerCase(), fingerprint, values_only: true, cells };
    });
  }

  /** Workbook-level integrity facts (TI controls): iterative calculation, protection, linked workbooks,
   *  external references, per-sheet hidden rows / columns, merged and comment counts, defined names.
   *  Every API newer than the manifest minimum is gated (null / [] when unsupported). One `Excel.run`. */
  async excel_get_workbook_integrity(input: X.WorkbookIntegrityInput = {}): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const wb = ctx.workbook;
        wb.load('name');
        const sheets = wb.worksheets;
        sheets.load('items/name,items/visibility');
        const app = wb.application;
        app.load('calculationMode');
        const names = wb.names;
        names.load('items/name,items/formula,items/type,items/visible');
        await ctx.sync();

        // Gated workbook facts, each in its own sync so one unsupported API never fails the others.
        let iterative: X.WorkbookIntegrityOutput['iterative_calculation'] = null;
        if (apiSupported('1.9')) {
          try {
            const it = app.iterativeCalculation;
            it.load(['enabled', 'maxIteration', 'maxChange']);
            await ctx.sync();
            iterative = { enabled: !!it.enabled, max_iterations: Number(it.maxIteration), max_change: Number(it.maxChange) };
          } catch {
            iterative = null;
          }
        }
        let workbookProtected: boolean | null = null;
        if (apiSupported('1.7')) {
          try {
            const p = wb.protection;
            p.load('protected');
            await ctx.sync();
            workbookProtected = !!p.protected;
          } catch {
            workbookProtected = null;
          }
        }
        let linked: string[] | null = null;
        if (apiSupported('1.19')) {
          try {
            const lw = wb.linkedWorkbooks;
            lw.load('items/id');
            await ctx.sync();
            linked = lw.items.map((l) => l.id);
          } catch {
            linked = null;
          }
        }

        // Per-sheet facts: used range, protection, merged areas.
        const per = sheets.items.map((ws) => {
          const used = ws.getUsedRangeOrNullObject(true);
          used.load(['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount']);
          const prot = ws.protection;
          prot.load('protected');
          const merged = ws.getRange().getMergedAreasOrNullObject();
          merged.load('areaCount');
          return { ws, used, prot, merged };
        });
        await ctx.sync();
        // Comments per sheet (ExcelApi 1.10), gated as a batch.
        const commentCounts: number[] = per.map(() => 0);
        if (apiSupported('1.10')) {
          try {
            const counts = per.map(({ ws }) => ws.comments.getCount());
            await ctx.sync();
            counts.forEach((c, i) => { commentCounts[i] = Number(c.value) || 0; });
          } catch {
            /* comments unsupported on this host */
          }
        }
        // Hidden rows / columns per sheet, one gated sync (sheets too tall to enumerate report []).
        const hidden: { rows: string[]; cols: string[] }[] = per.map(() => ({ rows: [], cols: [] }));
        if (apiSupported('1.9')) {
          try {
            const q = per.map(({ used }) => (used.isNullObject || used.rowCount > MAX_HIDDEN_FLAG_ROWS ? null : { rp: used.getRowProperties({ rowHidden: true }), cp: used.getColumnProperties({ columnHidden: true }) }));
            await ctx.sync();
            q.forEach((x, i) => {
              if (!x) return;
              const u = per[i].used;
              hidden[i] = { rows: hiddenSpans(x.rp.value.map((r) => !!r.rowHidden), u.rowIndex + 1, 'row'), cols: hiddenSpans(x.cp.value.map((c) => !!c.columnHidden), u.columnIndex + 1, 'col') };
            });
          } catch {
            /* keep [] */
          }
        }
        // External references: every formula of every used range, sheet by sheet, under a cell cap.
        let externalCount = 0;
        const samples: X.WorkbookIntegrityOutput['external_ref_samples'] = [];
        let scanned = 0;
        let truncated = false;
        if (input.scan_external_refs !== false) {
          for (const { ws, used } of per) {
            if (used.isNullObject) continue;
            const cells = used.rowCount * used.columnCount;
            if (scanned + cells > EXTERNAL_SCAN_MAX_CELLS) {
              truncated = true;
              break;
            }
            used.load('formulas');
            await ctx.sync();
            scanned += cells;
            const f = used.formulas as unknown[][];
            for (let r = 0; r < f.length; r++) {
              for (let c = 0; c < f[r].length; c++) {
                if (!hasExternalRef(f[r][c])) continue;
                externalCount++;
                if (samples.length < 10) samples.push({ sheet: ws.name, cell: `${colToLetter(used.columnIndex + c + 1)}${used.rowIndex + r + 1}`, formula: String(f[r][c]).slice(0, 120) });
              }
            }
          }
        }
        const broken: { name: string; refers_to: string }[] = [];
        const hiddenNames: string[] = [];
        const onSeries: { name: string; refers_to: string }[] = [];
        for (const n of names.items) {
          if (isBrokenName(String(n.type), n.formula)) broken.push({ name: n.name, refers_to: n.formula });
          if (n.visible === false) hiddenNames.push(n.name);
          if (nameOnSeries(n.formula)) onSeries.push({ name: n.name, refers_to: n.formula });
        }
        const out: X.WorkbookIntegrityOutput = {
          workbook_name: wb.name,
          file_extension: fileExtensionOf(wb.name),
          calculation_mode: String(app.calculationMode).toLowerCase(),
          iterative_calculation: iterative,
          workbook_protected: workbookProtected,
          linked_workbooks: linked,
          external_ref_count: externalCount,
          external_ref_samples: samples,
          sheets: per.map(({ ws, used, prot, merged }, i) => ({
            name: ws.name,
            visibility: String(ws.visibility).toLowerCase(),
            protected: !!prot.protected,
            rows: used.isNullObject ? 0 : used.rowCount,
            cols: used.isNullObject ? 0 : used.columnCount,
            hidden_rows: hidden[i].rows,
            hidden_cols: hidden[i].cols,
            merged_count: merged.isNullObject ? 0 : merged.areaCount,
            comments_count: commentCounts[i],
          })),
          names: { count: names.items.length, broken, hidden: hiddenNames, on_series: onSeries },
        };
        if (truncated) out.note = `External reference scan stopped at ${scanned} cells (cap ${EXTERNAL_SCAN_MAX_CELLS}); remaining sheets were not scanned.`;
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_get_workbook_integrity');
    }
  }

  /** The raw material of a cell dossier: value, formulas, format, hidden flags, merge, comment, data
   *  validation, direct precedents with values and a label guess, dependents, direct circularity.
   *  Every optional API is gated; one `Excel.run`. */
  async excel_inspect_cell(input: X.InspectCellInput): Promise<Json> {
    const split = splitSheetPrefix(String(input.cell ?? ''));
    const sheet = split.sheet ?? input.sheet;
    let cellA1: string;
    try {
      const c = parseCell(split.address);
      cellA1 = `${colToLetter(c.col)}${c.row}`;
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const maxPrec = Math.min(Math.max(1, Math.floor(Number(input.max_precedents) || 50)), MAX_INSPECT_PRECEDENTS);
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, sheet);
        const cell = ws.getRange(cellA1);
        cell.load(['values', 'valueTypes', 'formulas', 'formulasR1C1', 'numberFormat']);
        await ctx.sync();
        const formula = String(cell.formulas[0][0] ?? '');
        const isF = formula.startsWith('=');
        const r1c1 = String(cell.formulasR1C1[0][0] ?? '');

        let rowHidden = false;
        let colHidden = false;
        if (apiSupported('1.9')) {
          try {
            const rp = cell.getRowProperties({ rowHidden: true });
            const cp = cell.getColumnProperties({ columnHidden: true });
            await ctx.sync();
            rowHidden = !!rp.value[0]?.rowHidden;
            colHidden = !!cp.value[0]?.columnHidden;
          } catch {
            /* keep false */
          }
        }
        let mergedArea: string | null = null;
        try {
          const m = cell.getMergedAreasOrNullObject();
          m.load('areas/items/address');
          await ctx.sync();
          if (!m.isNullObject && m.areas.items.length) mergedArea = stripSheet(m.areas.items[0].address);
        } catch {
          mergedArea = null;
        }
        let comment: string | null = null;
        if (apiSupported('1.10')) {
          try {
            const c = ws.comments.getItemByCell(cellA1);
            c.load('content');
            await ctx.sync();
            comment = c.content || null;
          } catch {
            comment = null; // no comment on this cell
          }
        }
        let hasValidation = false;
        if (apiSupported('1.8')) {
          try {
            const dv = cell.dataValidation;
            dv.load('type');
            await ctx.sync();
            hasValidation = String(dv.type) !== 'None';
          } catch {
            hasValidation = false;
          }
        }

        // Direct precedents (ExcelApi 1.12): areas → cells (capped) → values, types, formulas, label.
        let precAddresses: string[] = [];
        if (isF && apiSupported('1.12')) {
          try {
            const p = cell.getDirectPrecedents();
            p.load('addresses');
            await ctx.sync();
            precAddresses = p.addresses ?? [];
          } catch {
            precAddresses = []; // no precedents (Office answers ItemNotFound)
          }
        }
        const precCount = areasCells(precAddresses);
        const expanded: { sheet: string | null; cell: string }[] = [];
        for (const a of precAddresses) {
          expanded.push(...expandArea(a, maxPrec - expanded.length));
          if (expanded.length >= maxPrec) break;
        }
        const precedents: X.InspectPrecedent[] = [];
        if (expanded.length) {
          const loaded = expanded.map((e) => {
            const pws = e.sheet ? ctx.workbook.worksheets.getItem(e.sheet) : ws;
            const r = pws.getRange(e.cell);
            r.load(['values', 'valueTypes', 'formulas']);
            const k = Math.min(LABEL_LOOKBACK_COLS, parseCell(e.cell).col - 1);
            const left = k > 0 ? r.getOffsetRange(0, -k).getResizedRange(0, k - 1) : null;
            left?.load('values');
            return { e, r, left, sheetName: e.sheet ?? sheet };
          });
          await ctx.sync();
          let flags = loaded.map(() => ({ row: false, col: false }));
          if (apiSupported('1.9')) {
            try {
              const q = loaded.map(({ r }) => ({ rp: r.getRowProperties({ rowHidden: true }), cp: r.getColumnProperties({ columnHidden: true }) }));
              await ctx.sync();
              flags = q.map((x) => ({ row: !!x.rp.value[0]?.rowHidden, col: !!x.cp.value[0]?.columnHidden }));
            } catch {
              /* keep false */
            }
          }
          loaded.forEach(({ e, r, left, sheetName }, i) => {
            const f = String(r.formulas[0][0] ?? '');
            precedents.push({
              address: qualify(sheetName, e.cell),
              value: r.values[0][0],
              value_type: String(r.valueTypes[0][0]),
              formula: f.startsWith('=') ? f : null,
              row_hidden: flags[i].row,
              col_hidden: flags[i].col,
              label: labelFromLeft(left ? (left.values[0] as unknown[]) : null),
            });
          });
        }

        // Direct dependents (ExcelApi 1.15) and circularity (the cell among its own precedents, all levels).
        let dependentsCount = 0;
        let dependentsSample: string[] = [];
        if (apiSupported('1.15')) {
          try {
            const d = cell.getDirectDependents();
            d.load('addresses');
            await ctx.sync();
            const addrs = d.addresses ?? [];
            dependentsCount = areasCells(addrs);
            dependentsSample = addrs.slice(0, 10);
          } catch {
            dependentsCount = 0; // no dependents
          }
        }
        let circular = false;
        if (isF && apiSupported('1.12')) {
          try {
            const all = cell.getPrecedents();
            all.load('addresses');
            await ctx.sync();
            circular = areasContain(all.addresses ?? [], sheet, cellA1);
          } catch {
            circular = false;
          }
        }
        const out: X.InspectCellOutput = {
          sheet,
          cell: cellA1,
          value: cell.values[0][0],
          value_type: String(cell.valueTypes[0][0]),
          formula: isF ? formula : null,
          formula_r1c1: r1c1.startsWith('=') ? r1c1 : null,
          number_format: String(cell.numberFormat[0][0] ?? 'General'),
          row_hidden: rowHidden,
          col_hidden: colHidden,
          merged_area: mergedArea,
          comment,
          has_data_validation: hasValidation,
          precedents,
          precedents_count: precCount,
          precedents_truncated: precCount > precedents.length,
          dependents_count: dependentsCount,
          dependents_sample: dependentsSample,
          circular,
        };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_inspect_cell');
    }
  }

  /** Write the server-side audit report into the `Audit` sheet (§6.2): the ToolRunner fetches the
   *  report, this method pours `layoutAuditSheet` into Excel (title, header, one row per finding with a
   *  hyperlink, severity fills, widths, frozen header, autofilter). `replace: false` on an existing
   *  sheet refuses with ItemAlreadyExists. One `Excel.run`. */
  async excel_write_audit_sheet_rows(input: A.WriteAuditSheetRowsInput): Promise<Json> {
    const name = String(input.sheet_name || AUDIT_SHEET_DEFAULT_NAME).trim();
    if (!name) return invalidInput('sheet_name is required');
    if (!input.report || !Array.isArray(input.report.rows)) return invalidInput('report with a rows array is required');
    const L = layoutAuditSheet(input.report);
    try {
      return await Excel.run(async (ctx) => {
        let ws = ctx.workbook.worksheets.getItemOrNullObject(name);
        await ctx.sync();
        if (ws.isNullObject) {
          ws = ctx.workbook.worksheets.add(name);
        } else if (input.replace === false) {
          throw Object.assign(new Error(`Worksheet '${name}' already exists`), { code: 'ItemAlreadyExists' });
        } else {
          try {
            ws.autoFilter.remove();
            await ctx.sync();
          } catch {
            /* no autofilter to remove */
          }
          ws.getRange().clear('All' as Excel.ClearApplyTo);
        }
        ws.getRange(L.address).formulas = L.grid as unknown as unknown[][];
        const title = ws.getRange(L.titleAddress).format.font;
        title.bold = true;
        title.size = 14;
        const header = ws.getRange(L.headerAddress).format;
        header.font.bold = true;
        header.fill.color = AUDIT_HEADER_FILL;
        for (const f of L.fills) ws.getRange(f.address).format.fill.color = f.color;
        // `autofitColumns` measures the whole column whatever range it is given (office-js#5999).
        // Harmless for B..H, which hold nothing but the table; column A is set explicitly below.
        ws.getRange(L.autofitAddress).format.autofitColumns();
        for (const c of L.fixedColumns) {
          const col = ws.getRange(`${c.column}:${c.column}`).format;
          col.columnWidth = charWidthToPoints(c.width);
          col.wrapText = c.wrap;
        }
        ws.freezePanes.unfreeze();
        ws.freezePanes.freezeRows(L.freezeRows);
        ws.activate();
        await ctx.sync();
        if (L.tableAddress) {
          // Row heights follow the wrapped free-text columns, so they are measured once the widths are applied.
          ws.getRange(L.tableAddress).format.autofitRows();
          await ctx.sync();
          try {
            ws.autoFilter.apply(ws.getRange(L.tableAddress));
            await ctx.sync();
          } catch {
            /* autofilter unsupported: the table is still readable */
          }
        }
        const out: A.WriteAuditSheetOutput = { status: 'ok', sheet: name, rows: L.dataRows, _changes: changes(name, L.address) };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_write_audit_sheet');
    }
  }

  /** The Audit Findings sheet of Sculpt's structural audit (layout C, `findingsSheet.ts`): written from the audit
   *  card the pane holds, not by the model. An existing sheet of that name is cleared and written again (the latest
   *  audit replaces the previous one). Sub-rows are grouped under their finding and collapsed when the host has
   *  row outlines (ExcelApi 1.10); without them they stay visible. One `Excel.run`. */
  async writeAuditFindingsSheet(view: AuditView): Promise<{ sheet: string; findings: number }> {
    const findings = await this.writeReportSheet(FINDINGS_SHEET_NAME, FINDINGS_HEADER_FILL, (name) => layoutFindingsSheet(view, name));
    return { sheet: FINDINGS_SHEET_NAME, findings };
  }

  /** The Audit Findings sheet the user already has, brought in line with a newer audit card (a fix marked, a fix
   *  undone): only the Status and Fix cells of the findings whose status moved are written (`findingsSheetEdits`),
   *  the rest of the sheet — what the user added included — is left as it is, and the user stays where they are.
   *  Null when the workbook has no such sheet; `findings` lists the ids whose row changed. One `Excel.run`. */
  async syncAuditFindingsSheet(view: AuditView): Promise<{ sheet: string; findings: string[] } | null> {
    return Excel.run(async (ctx) => {
      const ws = ctx.workbook.worksheets.getItemOrNullObject(FINDINGS_SHEET_NAME);
      await ctx.sync();
      if (ws.isNullObject) return null;
      const used = ws.getUsedRangeOrNullObject(true);
      used.load('rowIndex,columnIndex,rowCount,columnCount');
      await ctx.sync();
      if (used.isNullObject) return { sheet: FINDINGS_SHEET_NAME, findings: [] };
      const all = ws.getRangeByIndexes(0, 0, used.rowIndex + used.rowCount, used.columnIndex + used.columnCount);
      all.load('values');
      await ctx.sync();
      const { edits, updated } = findingsSheetEdits(all.values, view);
      for (const e of edits) {
        const cell = ws.getRange(e.address);
        cell.values = [[e.value]];
        if (e.fill) cell.format.fill.color = e.fill;
        else if (e.fill === null) cell.format.fill.clear();
      }
      if (edits.length) await ctx.sync();
      return { sheet: FINDINGS_SHEET_NAME, findings: updated };
    });
  }

  /** The Term Sheet Gaps sheet of Sculpt's term sheet reconciliation (`termsSheet.ts`): every term of the card the
   *  pane holds, the gaps first, written like the Audit Findings sheet (cleared and written again, candidates
   *  grouped under their term). */
  async writeTermsSheet(view: TermsView): Promise<{ sheet: string; terms: number }> {
    const terms = await this.writeReportSheet(TERMS_SHEET_NAME, TERMS_HEADER_FILL, (name) => layoutTermsSheet(view, name));
    return { sheet: TERMS_SHEET_NAME, terms };
  }

  /** A report sheet from its layout: an existing sheet of that name cleared and written again, title, header fill,
   *  status fills, widths, frozen header, autofilter, sub-rows grouped when the host has row outlines. One
   *  `Excel.run`; returns the report's row count. */
  private async writeReportSheet(sheetName: string, headerFill: string, layout: (workbookName: string) => FindingsSheetLayout): Promise<number> {
    return Excel.run(async (ctx) => {
      ctx.workbook.load('name');
      let ws = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
      await ctx.sync();
      const L = layout(ctx.workbook.name);
      if (ws.isNullObject) {
        ws = ctx.workbook.worksheets.add(sheetName);
      } else {
        try {
          ws.autoFilter.remove();
          await ctx.sync();
        } catch {
          /* no autofilter to remove */
        }
        ws.getRange().clear('All' as Excel.ClearApplyTo);
      }
      ws.getRange(L.address).formulas = L.grid as unknown as unknown[][];
      const title = ws.getRange('A1').format.font;
      title.bold = true;
      title.size = 14;
      const header = ws.getRange(L.headerAddress).format;
      header.font.bold = true;
      header.fill.color = headerFill;
      for (const f of L.fills) ws.getRange(f.address).format.fill.color = f.color;
      if (L.tableAddress) ws.getRange(L.autofitAddress).format.autofitColumns();
      for (const w of L.widths) {
        const col = ws.getRange(`${w.column}:${w.column}`).format;
        col.columnWidth = charWidthToPoints(w.width);
        col.wrapText = w.wrap;
      }
      ws.freezePanes.unfreeze();
      ws.freezePanes.freezeRows(L.freezeRows);
      ws.activate();
      await ctx.sync();
      if (L.tableAddress) {
        ws.getRange(L.tableAddress).format.autofitRows();
        await ctx.sync();
        try {
          ws.autoFilter.apply(ws.getRange(L.tableAddress));
          await ctx.sync();
        } catch {
          /* autofilter unsupported: the table is still readable */
        }
      }
      if (L.groups.length && apiSupported('1.10')) {
        try {
          for (const g of L.groups) ws.getRange(`${g.from}:${g.to}`).group(Excel.GroupOption.byRows);
          ws.showOutlineLevels(1, 0);
          await ctx.sync();
        } catch {
          /* no row outline on this host: the locations stay visible under their finding */
        }
      }
      return L.findingRows;
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Write — cells & formulas (W)
  // ────────────────────────────────────────────────────────────────────────

  async excel_write_range(input: X.WriteRangeInput): Promise<Json> {
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const shapeErr = checkShape(address, input.values);
    if (shapeErr) return invalidInput(shapeErr);
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const blocked = await guardOccupied(ctx, ws, input.sheet, address, input.overwrite);
        if (blocked) return blocked;
        const range = ws.getRange(address);
        range.values = asTextGrid(input.values);
        if (input.number_format) {
          const s = rangeShape(address);
          range.numberFormat = Array.from({ length: s.rows }, () => Array.from({ length: s.cols }, () => input.number_format as string));
        }
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, address) };
      });
    } catch (err) {
      return toToolError(err, 'excel_write_range');
    }
  }

  async excel_write_formulas(input: X.WriteFormulasInput): Promise<Json> {
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const s = rangeShape(address);
    if (input.formulas) {
      const shapeErr = checkShape(address, input.formulas);
      if (shapeErr) return invalidInput(shapeErr);
    } else if (!input.formula_r1c1) {
      return invalidInput('either formulas (2-D array) or formula_r1c1 (one pattern) is required');
    } else {
      // Fail fast with a precise message: Excel only answers "GeneralException" to A1 refs inside R1C1.
      const problem = r1c1Problem(input.formula_r1c1);
      if (problem) return invalidInput(problem);
    }
    // Sculpt conventions (D4): allow-list, justification-required functions, length / nesting caps.
    const lintOpts = {
      justification: input.justification ?? null,
      extendsExisting: input.extends_existing === true,
      copied: input.copied === true,
    };
    const lint = input.formulas
      ? input.formulas.flat().flatMap((f) => legacyProblems(f, lintOpts).map((p) => `${f.slice(0, 60)}: ${p}`))
      : legacyProblems(input.formula_r1c1 as string, lintOpts);
    if (lint.length) return invalidInput(lint.join('; '));
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const blocked = await guardOccupied(ctx, ws, input.sheet, address, input.overwrite);
        if (blocked) return blocked;
        const range = ws.getRange(address);
        if (input.formulas) range.formulas = input.formulas;
        else range.formulasR1C1 = Array.from({ length: s.rows }, () => Array.from({ length: s.cols }, () => input.formula_r1c1 as string));
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, address) };
      });
    } catch (err) {
      return toToolError(err, 'excel_write_formulas');
    }
  }

  /**
   * Scattered cells in ONE call (server `excel_write_cells`): {A1 cell: value}, a string starting with
   * '=' being an A1 formula for that cell. The tool for edits spread over a sheet — a header here, a
   * value there, one formula to extend — which used to cost dozens of single-cell `excel_write_range`
   * calls. The occupancy guard loads only the target cells (one sync, not one round trip per cell);
   * the before-image is journalled by ToolRunner as their bounding box.
   */
  async excel_write_cells(input: X.WriteCellsInput): Promise<Json> {
    const raw = input.cells;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return invalidInput('cells is required: {A1 cell: value}');
    const entries = Object.entries(raw as Record<string, X.CellValue>);
    if (!entries.length) return invalidInput('cells is empty');
    if (entries.length > MAX_WRITE_CELLS) {
      return invalidInput(`${entries.length} cells: at most ${MAX_WRITE_CELLS} per call — one rectangle of many cells goes to excel_write_range / excel_write_formulas`);
    }
    // Normalise every key to a single A1 cell ("b7" → "B7"); a range key belongs to another tool.
    const targets: { address: string; row: number; col: number; value: X.CellValue }[] = [];
    const seen = new Set<string>();
    for (const [key, value] of entries) {
      let ref;
      try {
        ref = parseRange(key);
      } catch {
        return invalidInput(`not a cell address: ${key}`);
      }
      if (ref.start.row !== ref.end.row || ref.start.col !== ref.end.col) {
        return invalidInput(`${key} is a range, not a single cell — use excel_write_range / excel_write_formulas`);
      }
      const address = rangeToA1(ref);
      if (seen.has(address)) return invalidInput(`${address} is listed twice`);
      seen.add(address);
      targets.push({ address, row: ref.start.row, col: ref.start.col, value });
    }
    // Sculpt conventions (D4), same lint as excel_write_formulas — but cells whose existing formula is
    // being edited were already checked server-side against that formula (its style is grandfathered).
    // Re-running the plain lint on them here would refuse what the server deliberately accepted.
    const grandfathered = new Set(
      Object.keys(input.existing ?? {})
        .filter((k) => isValidAddress(k))
        .map((k) => rangeToA1(parseRange(k))),
    );
    const lintOpts = { justification: input.justification ?? null };
    const problems: string[] = [];
    for (const t of targets) {
      if (typeof t.value !== 'string' || !t.value.startsWith('=')) continue;
      if (grandfathered.has(t.address)) continue;
      problems.push(...legacyProblems(t.value, lintOpts).map((p) => `${t.address}: ${p}`));
    }
    if (problems.length) return invalidInput(problems.join('; '));
    const box = boundingBox(targets.map((t) => t.address));
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        if (input.overwrite !== true) {
          // One load per target cell, batched into a single sync — the bounding box may be mostly
          // empty space, and only the cells actually written may block the write.
          const probes = targets.map((t) => {
            const r = ws.getRange(t.address);
            r.load(['values', 'formulas']);
            return r;
          });
          await ctx.sync();
          const occupied = targets
            .map((t, i) => ({ t, value: (probes[i].values as unknown[][])?.[0]?.[0], formula: (probes[i].formulas as unknown[][])?.[0]?.[0] }))
            .filter((p) => isOccupied(p.value, p.formula))
            .map((p) => ({ address: p.t.address, value: p.value }));
          if (occupied.length) return occupiedError(input.sheet, box, occupied) as unknown as Json;
        }
        for (const t of targets) {
          const range = ws.getRange(t.address);
          if (typeof t.value === 'string' && t.value.startsWith('=')) range.formulas = [[t.value]];
          else range.values = [[asText(t.value)]];
          if (input.number_format) range.numberFormat = [[input.number_format]];
        }
        await ctx.sync();
        // `address` is the bounding box (what the UI highlights); `cells` is what was really written.
        return { status: 'ok', _changes: { sheet: input.sheet, address: box, cells: targets.length } };
      });
    } catch (err) {
      return toToolError(err, 'excel_write_cells');
    }
  }

  /** Whole model block in one call (domain #32 / D3): labels (+ units) in the anchor column, one R1C1
   *  pattern or per-period constants per row over the axis. Refuses to overwrite non-empty cells without
   *  the user's acknowledgement (#36), verifies error cells after writing. One `Excel.run`. */
  async excel_write_block(input: X.WriteBlockInput): Promise<Json> {
    const problems = validateBlock(input);
    if (problems.length) return invalidInput(problems.join('; '));
    const L = layoutBlock(input);
    const insert = input.mode === 'insert_rows';
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        if (insert) {
          ws.getRange(`${L.topRow}:${L.topRow + L.rowCount - 1}`).insert('Down' as Excel.InsertShiftDirection);
          await ctx.sync();
        } else {
          const target = ws.getRange(L.targetAddress);
          target.load(['values', 'formulas']);
          await ctx.sync();
          const occupied = occupiedCells(target.values as unknown[][], target.formulas as unknown[][], L.targetAddress);
          if (occupied.length > 0 && !input.overwrite_ack) {
            return overwriteAckError(input.sheet, L.targetAddress, occupied) as unknown as Json;
          }
        }
        if (L.titleCell) ws.getRange(L.titleCell).values = [[asText(input.title as string)]];
        for (const r of L.rows) {
          ws.getRange(cellA1(L.labelCol, r.row)).values = [[asText(r.label)]];
          if (L.unitCol !== null) ws.getRange(cellA1(L.unitCol, r.row)).values = [[asText(r.unit ?? '')]];
          const axis = ws.getRange(`${cellA1(L.firstCol, r.row)}:${cellA1(L.lastCol, r.row)}`);
          if (r.formula) axis.formulasR1C1 = [Array.from({ length: L.periods }, () => r.formula as string)];
          else if (r.values) axis.values = asTextGrid([r.values.length === 1 ? Array.from({ length: L.periods }, () => r.values![0]) : r.values]);
          if (r.fmt) axis.numberFormat = [Array.from({ length: L.periods }, () => r.fmt as string)];
        }
        await ctx.sync();
        const check = ws.getRange(L.axisAddress);
        check.load('values');
        await ctx.sync();
        const errors = errorCells(check.values as X.CellValue[][], { row: L.firstRow, col: L.firstCol });
        // D4: a justified OFFSET / INDIRECT / … gets its reason as a note on the label cell (never blocks the write).
        const justified = L.rows.filter((r) => r.justification && r.formula);
        if (justified.length) {
          try {
            for (const r of justified) ws.comments.add(cellA1(L.labelCol, r.row), justificationNote(r.formula as string, r.justification as string));
            await ctx.sync();
          } catch {
            /* comments unsupported on this host: the justification stays in the card / chat */
          }
        }
        const out: X.WriteBlockOutput = {
          status: 'ok',
          written_rows: L.rows.length,
          range: L.targetAddress,
          inserted_rows: insert ? { from: L.topRow, count: L.rowCount } : null,
          verification: { ok: errors.length === 0, error_cells: errors },
          _changes: changes(input.sheet, L.targetAddress),
        };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_write_block');
    }
  }

  /** Read-only pre-flight of a block write: how many non-empty cells an in-place write would overwrite
   *  (what the approval card shows next to its acknowledgement checkbox, #36). Never throws. */
  async previewWriteBlock(input: X.WriteBlockInput): Promise<X.OverwritePreview> {
    try {
      if (validateBlock(input).length) return { cells: 0, address: '', sample: [] };
      const L = layoutBlock(input);
      if (input.mode === 'insert_rows') return { cells: 0, address: L.targetAddress, sample: [] };
      return await Excel.run(async (ctx) => {
        const target = sheetOf(ctx, input.sheet).getRange(L.targetAddress);
        target.load(['values', 'formulas']);
        await ctx.sync();
        return overwritePreview(occupiedCells(target.values as unknown[][], target.formulas as unknown[][], L.targetAddress), L.targetAddress);
      });
    } catch {
      return { cells: 0, address: '', sample: [] };
    }
  }

  async excel_fill_range(input: X.FillRangeInput): Promise<Json> {
    try {
      parseRange(input.source);
      parseRange(input.destination);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const destination = rangeToA1(parseRange(input.destination));
        // The source lies inside the destination: only the cells to be filled are checked.
        const blocked = await guardOccupied(ctx, ws, input.sheet, destination, input.overwrite, [stripSheet(input.source)]);
        if (blocked) return blocked;
        const src = ws.getRange(stripSheet(input.source));
        const type = input.fill_type === 'series' ? 'FillSeries' : input.fill_type === 'copy' ? 'FillCopy' : 'FillDefault';
        src.autoFill(stripSheet(input.destination), type as Excel.AutoFillType);
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, rangeToA1(parseRange(input.destination))) };
      });
    } catch (err) {
      return toToolError(err, 'excel_fill_range');
    }
  }

  async excel_copy_range(input: X.CopyRangeInput): Promise<Json> {
    try {
      parseRange(input.source);
      parseRange(input.dest);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const destSheet = sheetOf(ctx, input.dest_sheet);
        // `dest` may be a single anchor cell: the written area is the source shape (swapped when transposed).
        const written = copyDestination(stripSheet(input.source), stripSheet(input.dest), !!input.transpose);
        if (input.paste !== 'formats') {
          const blocked = await guardOccupied(ctx, destSheet, input.dest_sheet, written, input.overwrite);
          if (blocked) return blocked;
        }
        const src = sheetOf(ctx, input.source_sheet).getRange(stripSheet(input.source));
        const dst = destSheet.getRange(stripSheet(input.dest));
        const type = input.paste === 'values' ? 'Values' : input.paste === 'formulas' ? 'Formulas' : input.paste === 'formats' ? 'Formats' : 'All';
        dst.copyFrom(src, type as Excel.RangeCopyType, false, !!input.transpose);
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.dest_sheet, written) };
      });
    } catch (err) {
      return toToolError(err, 'excel_copy_range');
    }
  }

  async excel_clear_range(input: X.ClearRangeInput): Promise<Json> {
    const areas = areasOrError(input.address);
    if (!Array.isArray(areas)) return areas;
    try {
      return await Excel.run(async (ctx) => {
        const range = rangeOrAreas(sheetOf(ctx, input.sheet), areas);
        const apply = input.apply_to === 'formats' ? 'Formats' : input.apply_to === 'all' ? 'All' : 'Contents';
        range.clear(apply as Excel.ClearApplyTo);
        await ctx.sync();
        return { status: 'ok', _changes: areaChanges(input.sheet, areas) };
      });
    } catch (err) {
      return toToolError(err, 'excel_clear_range');
    }
  }

  async excel_set_number_format(input: X.SetNumberFormatInput): Promise<Json> {
    const areas = areasOrError(input.address);
    if (!Array.isArray(areas)) return areas;
    if (typeof input.number_format !== 'string') {
      // A matrix only makes sense for one area.
      if (areas.length > 1) return invalidInput('a number_format matrix requires a single-area address; pass one format code for several areas');
      const err = checkShape(areas[0], input.number_format);
      if (err) return invalidInput(err);
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        if (typeof input.number_format === 'string') {
          const code = input.number_format;
          for (const address of areas) {
            const s = rangeShape(address);
            ws.getRange(address).numberFormat = Array.from({ length: s.rows }, () => Array.from({ length: s.cols }, () => code));
          }
        } else {
          ws.getRange(areas[0]).numberFormat = input.number_format;
        }
        await ctx.sync();
        return { status: 'ok', _changes: areaChanges(input.sheet, areas) };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_number_format');
    }
  }

  async excel_format_range(input: X.FormatRangeInput): Promise<Json> {
    const areas = areasOrError(input.address);
    if (!Array.isArray(areas)) return areas;
    try {
      return await Excel.run(async (ctx) => {
        const range = rangeOrAreas(sheetOf(ctx, input.sheet), areas);
        const f = range.format;
        if (input.bold != null) f.font.bold = input.bold;
        if (input.italic != null) f.font.italic = input.italic;
        if (input.font_size != null) f.font.size = input.font_size;
        if (input.font_name) f.font.name = input.font_name;
        if (input.font_color) f.font.color = input.font_color;
        if (input.fill_color) f.fill.color = input.fill_color;
        if (input.h_align) f.horizontalAlignment = ({ left: 'Left', center: 'Center', right: 'Right', general: 'General' }[input.h_align] ?? 'General') as Excel.HorizontalAlignment;
        if (input.v_align) f.verticalAlignment = ({ top: 'Top', center: 'Center', bottom: 'Bottom' }[input.v_align] ?? 'Bottom') as Excel.VerticalAlignment;
        if (input.wrap != null) f.wrapText = input.wrap;
        if (input.indent != null) f.indentLevel = input.indent;
        if (input.borders) {
          const edges: Record<X.BordersPreset, string[]> = {
            none: ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight', 'InsideHorizontal', 'InsideVertical'],
            outline: ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight'],
            all: ['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight', 'InsideHorizontal', 'InsideVertical'],
            top: ['EdgeTop'],
            bottom: ['EdgeBottom'],
            left: ['EdgeLeft'],
            right: ['EdgeRight'],
            inside: ['InsideHorizontal', 'InsideVertical'],
          };
          for (const edge of edges[input.borders] ?? []) {
            const b = f.borders.getItem(edge as Excel.BorderIndex);
            b.style = (input.borders === 'none' ? 'None' : 'Continuous') as Excel.BorderLineStyle;
            if (input.borders !== 'none') {
              b.weight = 'Thin' as Excel.BorderWeight;
              if (input.border_color) b.color = input.border_color;
            }
          }
        }
        await ctx.sync();
        return { status: 'ok', _changes: areaChanges(input.sheet, areas) };
      });
    } catch (err) {
      return toToolError(err, 'excel_format_range');
    }
  }

  async excel_set_column_widths(input: X.SetColumnWidthsInput): Promise<Json> {
    let address: string;
    try {
      address = wholeColumnsAddress(input.columns);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        if (input.width === 'autofit') range.format.autofitColumns();
        else range.format.columnWidth = charWidthToPoints(Number(input.width));
        await ctx.sync();
        return { status: 'ok', _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_column_widths');
    }
  }

  async excel_set_row_heights(input: X.SetRowHeightsInput): Promise<Json> {
    let address: string;
    try {
      address = wholeRowsAddress(input.rows);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        if (input.height === 'autofit') range.format.autofitRows();
        else range.format.rowHeight = Number(input.height);
        await ctx.sync();
        return { status: 'ok', _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_row_heights');
    }
  }

  async excel_merge_cells(input: X.MergeInput): Promise<Json> {
    return this.mergeOrUnmerge(input, true);
  }

  async excel_unmerge_cells(input: X.MergeInput): Promise<Json> {
    return this.mergeOrUnmerge(input, false);
  }

  private async mergeOrUnmerge(input: X.MergeInput, merge: boolean): Promise<Json> {
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        if (merge) range.merge(false);
        else range.unmerge();
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, address) };
      });
    } catch (err) {
      return toToolError(err, merge ? 'excel_merge_cells' : 'excel_unmerge_cells');
    }
  }

  async excel_add_data_validation(input: X.DataValidationInput): Promise<Json> {
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const rule = input.rule;
    if (!rule || !rule.type) return invalidInput('rule.type is required');
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        const dv = range.dataValidation;
        const op = (rule.operator ?? 'between') as Excel.DataValidationOperator;
        const basic = { operator: op, formula1: rule.formula1 ?? '', formula2: rule.formula2 };
        let r: Excel.DataValidationRule;
        switch (rule.type) {
          case 'list':
            if (!rule.source) throw Object.assign(new Error('rule.source is required for list validation'), { code: 'InvalidArgument' });
            r = { list: { inCellDropDown: true, source: rule.source } };
            break;
          case 'whole_number': r = { wholeNumber: basic as Excel.BasicDataValidation }; break;
          case 'decimal': r = { decimal: basic as Excel.BasicDataValidation }; break;
          case 'date': r = { date: basic as Excel.DateTimeDataValidation }; break;
          case 'custom': r = { custom: { formula: rule.formula1 ?? '' } }; break;
          default: throw Object.assign(new Error(`unknown rule.type ${String(rule.type)}`), { code: 'InvalidArgument' });
        }
        dv.rule = r;
        if (rule.error_message) dv.errorAlert = { message: rule.error_message, showAlert: true, style: 'Stop' as Excel.DataValidationAlertStyle, title: 'Invalid value' };
        if (rule.prompt) dv.prompt = { message: rule.prompt, showPrompt: true, title: '' };
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, address) };
      });
    } catch (err) {
      return toToolError(err, 'excel_add_data_validation');
    }
  }

  async excel_add_comment(input: X.CommentInput): Promise<Json> {
    try {
      parseCell(input.cell);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    if (!input.text) return invalidInput('text is required');
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.comments.add(stripSheet(input.cell), input.text as string);
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, stripSheet(input.cell)) };
      });
    } catch (err) {
      return toToolError(err, 'excel_add_comment');
    }
  }

  async excel_delete_comment(input: X.CommentInput): Promise<Json> {
    try {
      parseCell(input.cell);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.comments.getItemByCell(stripSheet(input.cell)).delete();
        await ctx.sync();
        return { status: 'ok', _changes: changes(input.sheet, stripSheet(input.cell)) };
      });
    } catch (err) {
      return toToolError(err, 'excel_delete_comment');
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // Write — structure
  // ────────────────────────────────────────────────────────────────────────

  private static rowOrColIndex(at: number | string, kind: 'row' | 'col'): number {
    if (typeof at === 'number') return at;
    const s = String(at).trim();
    if (/^\d+$/.test(s)) return parseInt(s, 10);
    if (/^[A-Za-z]{1,3}$/.test(s)) return parseCell(`${s}1`).col;
    const c = parseRange(s).start;
    return kind === 'row' ? c.row : c.col;
  }

  async excel_insert_rows(input: X.InsertInput): Promise<Json> {
    return this.insertRowsOrCols(input, 'row');
  }

  async excel_insert_columns(input: X.InsertInput): Promise<Json> {
    return this.insertRowsOrCols(input, 'col');
  }

  private async insertRowsOrCols(input: X.InsertInput, kind: 'row' | 'col'): Promise<Json> {
    let idx: number;
    try {
      idx = ExcelToolExecutor.rowOrColIndex(input.at, kind);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const count = Math.max(1, Math.floor(input.count ?? 1));
    const address = kind === 'row' ? `${idx}:${idx + count - 1}` : `${colToLetter(idx)}:${colToLetter(idx + count - 1)}`;
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        range.insert((kind === 'row' ? 'Down' : 'Right') as Excel.InsertShiftDirection);
        await ctx.sync();
        return { status: 'ok', inserted: count, _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, kind === 'row' ? 'excel_insert_rows' : 'excel_insert_columns');
    }
  }

  async excel_delete_rows(input: X.DeleteRangeInput): Promise<Json> {
    return this.deleteRowsOrCols(input, 'row');
  }

  async excel_delete_columns(input: X.DeleteRangeInput): Promise<Json> {
    return this.deleteRowsOrCols(input, 'col');
  }

  private async deleteRowsOrCols(input: X.DeleteRangeInput, kind: 'row' | 'col'): Promise<Json> {
    let address: string;
    try {
      const r = parseRange(input.address);
      address = kind === 'row' ? `${r.start.row}:${r.end.row}` : `${colToLetter(r.start.col)}:${colToLetter(r.end.col)}`;
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        sheetOf(ctx, input.sheet).getRange(address).delete((kind === 'row' ? 'Up' : 'Left') as Excel.DeleteShiftDirection);
        await ctx.sync();
        return { status: 'ok', _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, kind === 'row' ? 'excel_delete_rows' : 'excel_delete_columns');
    }
  }

  async excel_create_sheet(input: X.CreateSheetInput): Promise<Json> {
    if (!input.name) return invalidInput('name is required');
    try {
      return await Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.add(input.name);
        if (input.position === 'start') ws.position = 0;
        else if (typeof input.position === 'number') ws.position = input.position;
        if (input.activate !== false) ws.activate();
        ws.load(['name', 'position']);
        await ctx.sync();
        return { status: 'ok', sheet: ws.name, position: ws.position, _changes: { sheet: ws.name, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_create_sheet');
    }
  }

  async excel_rename_sheet(input: X.RenameSheetInput): Promise<Json> {
    if (!input.new_name) return invalidInput('new_name is required');
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.name = input.new_name;
        await ctx.sync();
        return { status: 'ok', sheet: input.new_name, _changes: { sheet: input.new_name, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_rename_sheet');
    }
  }

  async excel_move_sheet(input: X.MoveSheetInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.position = Math.max(0, Math.floor(input.position));
        ws.load('position');
        await ctx.sync();
        return { status: 'ok', sheet: input.sheet, position: ws.position, _changes: { sheet: input.sheet, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_move_sheet');
    }
  }

  async excel_set_sheet_visibility(input: X.SheetVisibilityInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.visibility = (input.visible ? 'Visible' : 'Hidden') as Excel.SheetVisibility;
        await ctx.sync();
        return { status: 'ok', sheet: input.sheet, visible: input.visible, _changes: { sheet: input.sheet, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_sheet_visibility');
    }
  }

  async excel_delete_sheet(input: X.DeleteSheetInput): Promise<Json> {
    if (!input.name) return invalidInput('name is required');
    try {
      return await Excel.run(async (ctx) => {
        const ws = ctx.workbook.worksheets.getItemOrNullObject(input.name);
        const count = ctx.workbook.worksheets.getCount();
        await ctx.sync();
        if (ws.isNullObject) throw Object.assign(new Error(`Worksheet '${input.name}' was not found`), { code: 'ItemNotFound' });
        if (count.value <= 1) throw Object.assign(new Error('A workbook must keep at least one sheet'), { code: 'InvalidOperation' });
        ws.delete();
        await ctx.sync();
        return { status: 'ok', deleted: input.name };
      });
    } catch (err) {
      return toToolError(err, 'excel_delete_sheet');
    }
  }

  /** `excel_insert_model` (insertModel.ts): a model filled on the server from a template, inserted into an EMPTY
   *  workbook. Three steps: the emptiness check (its own run, so nothing waits on Excel during the download), the
   *  file fetched through `fill` (ToolRunner injects ApiService), then one run that checks again, inserts the sheets,
   *  deletes the empty ones that were there and reads the key cells and the checks back once Excel has calculated.
   *  Not journalled: deleting and inserting sheets is structural, the change journal cannot take it back. */
  async excel_insert_model(input: X.InsertModelInput, fill?: X.TemplateFiller): Promise<Json> {
    const problem = insertModelProblem(input);
    if (problem) return invalidInput(problem);
    if (!fill) return notConfiguredError();
    if (!apiSupported(INSERT_MODEL_API)) return apiNotSupportedError();
    try {
      const reasons = nonEmptyReasons(await Excel.run((ctx) => readWorkbookContent(ctx)));
      if (reasons.length) return workbookNotEmptyError(reasons);
    } catch (err) {
      return toToolError(err, 'excel_insert_model');
    }

    let filled: X.FilledTemplate;
    try {
      filled = await fill(input.template, input.technology, input.inputs ?? {});
    } catch (err) {
      return fillError(err, input.template);
    }
    const bad = filledTemplateProblem(filled);
    if (bad) return invalidResponseError(bad);

    try {
      return await Excel.run(async (ctx) => {
        // The user may have typed into the empty sheet while the file downloaded.
        const reasons = nonEmptyReasons(await readWorkbookContent(ctx));
        if (reasons.length) return workbookNotEmptyError(reasons);
        const wb = ctx.workbook;
        const sheets = wb.worksheets;
        sheets.load('items/name');
        await ctx.sync();
        const before = sheets.items.map((ws) => ws.name);
        const incoming = filled.sheets?.length ? filled.sheets : input.sheets;
        const renamed = new Map(placeholderRenames(before, incoming));
        for (const [from, to] of renamed) sheets.getItem(from).name = to;
        const ids = wb.insertWorksheetsFromBase64(filled.file_base64, { positionType: Excel.WorksheetPositionType.end });
        await ctx.sync();
        const added = ids.value.map((id) => sheets.getItem(id));
        for (const ws of added) ws.load('name');
        await ctx.sync();
        const inserted = added.map((ws) => ws.name);
        const first = inserted.includes(input.sheets[0]) ? input.sheets[0] : inserted[0];
        if (!first) throw Object.assign(new Error('the template file holds no sheet'), { code: 'InvalidOperation' });
        sheets.getItem(first).activate();
        for (const name of before) sheets.getItem(renamed.get(name) ?? name).delete();
        await ctx.sync();
        try {
          wb.application.calculate(Excel.CalculationType.full);
          await ctx.sync();
        } catch {
          // A host that refuses the full calculation still calculates automatically; the reads below sync after it.
        }
        const readBack = input.read_back ?? [];
        const checks = input.checks ?? [];
        const values = await readCellRefs(ctx, [...readBack.map((r) => r.cell), ...checks]);
        return insertModelResult(input, inserted, first, values.slice(0, readBack.length), values.slice(readBack.length)) as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_insert_model');
    }
  }

  async excel_activate_sheet(input: X.ActivateSheetInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        sheetOf(ctx, input.sheet).activate();
        await ctx.sync();
        return { status: 'ok', sheet: input.sheet };
      });
    } catch (err) {
      return toToolError(err, 'excel_activate_sheet');
    }
  }

  async excel_select_range(input: X.SelectRangeInput): Promise<Json> {
    const areas = areasOrError(input.address);
    if (!Array.isArray(areas)) return areas;
    const address = areas.join(',');
    try {
      await this.highlight(input.sheet, address);
      return { status: 'ok', sheet: input.sheet, address };
    } catch (err) {
      return toToolError(err, 'excel_select_range');
    }
  }

  async excel_freeze_panes(input: X.FreezePanesInput): Promise<Json> {
    const rows = Math.max(0, Math.floor(input.rows ?? 0));
    const cols = Math.max(0, Math.floor(input.columns ?? 0));
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        ws.freezePanes.unfreeze();
        if (rows && cols) ws.freezePanes.freezeAt(ws.getRange(`A1:${colToLetter(cols)}${rows}`));
        else if (rows) ws.freezePanes.freezeRows(rows);
        else if (cols) ws.freezePanes.freezeColumns(cols);
        await ctx.sync();
        return { status: 'ok', sheet: input.sheet, rows, columns: cols, _changes: { sheet: input.sheet, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_freeze_panes');
    }
  }

  async excel_define_name(input: X.DefineNameInput): Promise<Json> {
    if (!input.name || !/^[A-Za-z_\\][A-Za-z0-9_.\\]*$/.test(input.name)) return invalidInput('name must start with a letter or underscore and contain only letters, digits, underscores or periods');
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const range = ws.getRange(address);
        const coll = input.scope === 'sheet' ? ws.names : ctx.workbook.names;
        const item = coll.add(input.name, range, input.comment ?? undefined);
        item.load(['name', 'formula']);
        await ctx.sync();
        return { status: 'ok', name: item.name, refers_to: item.formula, _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_define_name');
    }
  }

  async excel_delete_name(input: X.DeleteNameInput): Promise<Json> {
    if (!input.name) return invalidInput('name is required');
    try {
      return await Excel.run(async (ctx) => {
        const coll = input.sheet ? sheetOf(ctx, input.sheet).names : ctx.workbook.names;
        coll.getItem(input.name).delete();
        await ctx.sync();
        return { status: 'ok', deleted: input.name };
      });
    } catch (err) {
      return toToolError(err, 'excel_delete_name');
    }
  }

  /** Put a rectangle back as a before-image photographed it: the undo of an operation, built by the server
   *  from the journal (sculpt-api `operations/undo.py`). No overwrite guard and no formula lint — it restores
   *  what was there, and the journal keeps a before-image of the restore itself. */
  async excel_restore_snapshot(input: X.RestoreSnapshotInput): Promise<Json> {
    if (!input.sheet || !input.address) return invalidInput('sheet and address are required');
    if (!Array.isArray(input.formulas) || !input.formulas.length) return invalidInput('formulas (a 2-D array) is required');
    const address = stripSheet(input.address);
    const shapeProblem = checkShape(address, input.formulas);
    if (shapeProblem) return invalidInput(shapeProblem);
    const formats = Array.isArray(input.number_formats) && input.number_formats.length ? input.number_formats : null;
    if (formats) {
      const formatProblem = checkShape(address, formats);
      if (formatProblem) return invalidInput(`number_formats: ${formatProblem}`);
    }
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        range.formulas = input.formulas as string[][];
        if (formats) range.numberFormat = formats;
        await ctx.sync();
        return { status: 'ok', _changes: { sheet: input.sheet, address, cells: cellCount(address) } };
      });
    } catch (err) {
      return toToolError(err, 'excel_restore_snapshot');
    }
  }

  async excel_create_table(input: X.CreateTableInput): Promise<Json> {
    let address: string;
    try {
      address = rangeToA1(parseRange(input.address));
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const t = ws.tables.add(`${input.sheet.includes(' ') ? `'${input.sheet}'` : input.sheet}!${address}`, input.has_headers !== false);
        if (input.name) t.name = input.name;
        if (input.style) t.style = input.style;
        t.load('name');
        await ctx.sync();
        return { status: 'ok', table: t.name, _changes: changes(input.sheet, address) };
      });
    } catch (err) {
      return toToolError(err, 'excel_create_table');
    }
  }

  async excel_add_table_rows(input: X.AddTableRowsInput): Promise<Json> {
    if (!input.table_name) return invalidInput('table_name is required');
    if (!Array.isArray(input.values) || !input.values.length) return invalidInput('values must be a non-empty 2-D array');
    try {
      return await Excel.run(async (ctx) => {
        const t = ctx.workbook.tables.getItem(input.table_name);
        t.rows.add(undefined, input.values.map((r) => r.map((v) => (v === null ? '' : v))));
        const ws = t.worksheet;
        ws.load('name');
        const r = t.getRange();
        r.load('address');
        await ctx.sync();
        return { status: 'ok', added: input.values.length, _changes: changes(ws.name, stripSheet(r.address)) };
      });
    } catch (err) {
      return toToolError(err, 'excel_add_table_rows');
    }
  }

  async excel_delete_table(input: X.DeleteTableInput): Promise<Json> {
    if (!input.table_name) return invalidInput('table_name is required');
    try {
      return await Excel.run(async (ctx) => {
        const t = ctx.workbook.tables.getItem(input.table_name);
        if (input.keep_values) t.convertToRange();
        else t.delete();
        await ctx.sync();
        return { status: 'ok', deleted: input.table_name, values_kept: !!input.keep_values };
      });
    } catch (err) {
      return toToolError(err, 'excel_delete_table');
    }
  }

  async excel_create_chart(input: X.CreateChartInput): Promise<Json> {
    const data = chartDataOf(input);
    if (!data.ok) return data.error;
    if (input.secondary_series?.length && input.chart_type !== 'combo') return invalidInput("secondary_series requires chart_type 'combo'");
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        // The chart lives on `sheet`; its data may sit on another sheet (dashboards never mirror data).
        const dataWs = sheetOf(ctx, data.sheet);
        const firstArea = dataWs.getRange(data.areas[0]);
        const chart = ws.charts.add(chartTypeOf(input.chart_type) as Excel.ChartType, firstArea, seriesByOf(input.series_by) as Excel.ChartSeriesBy);
        if (input.name) chart.name = input.name;
        if (input.title) {
          chart.title.text = input.title;
          chart.title.visible = true;
        }
        const pos = input.position;
        if (pos?.anchor_cell) chart.setPosition(stripSheet(pos.anchor_cell));
        if (pos?.width) chart.width = pos.width;
        if (pos?.height) chart.height = pos.height;
        await ctx.sync();
        const notes: string[] = [];
        if (data.areas.length > 1) await addExtraAreaSeries(ctx, chart, dataWs, data.areas, input.series_by, notes);
        await applyChartStyle(ctx, chart, input, notes);
        chart.load('name');
        await ctx.sync();
        return { status: 'ok', chart: chart.name, sheet: input.sheet, data_sheet: data.sheet, data_range: data.areas.join(','), notes, _changes: { sheet: input.sheet, address: stripSheet(input.position?.anchor_cell || 'A1'), cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_create_chart');
    }
  }

  async excel_update_chart(input: X.UpdateChartInput): Promise<Json> {
    if (!input.chart_name) return invalidInput('chart_name is required');
    let data: { sheet: string; areas: string[] } | null = null;
    if (input.data_range) {
      const d = chartDataOf({ sheet: input.sheet, data_sheet: input.data_sheet, data_range: input.data_range });
      if (!d.ok) return d.error;
      data = { sheet: d.sheet, areas: d.areas };
    }
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        const chart = ws.charts.getItem(input.chart_name);
        if (input.title != null) {
          chart.title.text = input.title;
          chart.title.visible = !!input.title;
        }
        if (input.chart_type) chart.chartType = chartTypeOf(input.chart_type) as Excel.ChartType;
        const notes: string[] = [];
        if (data) {
          const dataWs = sheetOf(ctx, data.sheet);
          chart.setData(dataWs.getRange(data.areas[0]), seriesByOf(input.series_by ?? 'auto') as Excel.ChartSeriesBy);
          await ctx.sync();
          if (data.areas.length > 1) await addExtraAreaSeries(ctx, chart, dataWs, data.areas, input.series_by ?? undefined, notes);
        }
        if (input.show_legend != null) chart.legend.visible = input.show_legend;
        const pos = input.position;
        if (pos?.anchor_cell) chart.setPosition(stripSheet(pos.anchor_cell));
        if (pos?.width) chart.width = pos.width;
        if (pos?.height) chart.height = pos.height;
        await ctx.sync();
        await applyChartStyle(ctx, chart, input, notes);
        await ctx.sync();
        return { status: 'ok', chart: input.chart_name, notes, _changes: { sheet: input.sheet, address: pos?.anchor_cell ?? 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_update_chart');
    }
  }

  async excel_delete_chart(input: X.DeleteChartInput): Promise<Json> {
    if (!input.chart_name) return invalidInput('chart_name is required');
    try {
      return await Excel.run(async (ctx) => {
        sheetOf(ctx, input.sheet).charts.getItem(input.chart_name).delete();
        await ctx.sync();
        return { status: 'ok', deleted: input.chart_name };
      });
    } catch (err) {
      return toToolError(err, 'excel_delete_chart');
    }
  }

  async excel_group_rows(input: X.GroupInput): Promise<Json> {
    return this.group(input, 'row');
  }

  async excel_group_columns(input: X.GroupInput): Promise<Json> {
    return this.group(input, 'col');
  }

  private async group(input: X.GroupInput, kind: 'row' | 'col'): Promise<Json> {
    let address: string;
    try {
      const r = parseRange(input.address);
      address = kind === 'row' ? `${r.start.row}:${r.end.row}` : `${colToLetter(r.start.col)}:${colToLetter(r.end.col)}`;
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    try {
      return await Excel.run(async (ctx) => {
        const range = sheetOf(ctx, input.sheet).getRange(address);
        const opt = (kind === 'row' ? 'ByRows' : 'ByColumns') as Excel.GroupOption;
        range.group(opt);
        if (input.collapse) range.hideGroupDetails(opt);
        await ctx.sync();
        return { status: 'ok', _changes: { sheet: input.sheet, address, cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, kind === 'row' ? 'excel_group_rows' : 'excel_group_columns');
    }
  }

  async excel_set_protection(input: X.ProtectionInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        const ws = sheetOf(ctx, input.sheet);
        if (input.protect) ws.protection.protect();
        else ws.protection.unprotect();
        await ctx.sync();
        return { status: 'ok', sheet: input.sheet, protected: input.protect, _changes: { sheet: input.sheet, address: 'A1', cells: 0 } };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_protection');
    }
  }

  /** Copy-paste convergence of a circular module (domain #35 / #39): switch = 1, then repeat { circ ← live
   *  values; full recalculation; read the delta cell } until the delta is within tolerance. Refuses when
   *  Excel's iterative calculation is on; restores the circ block on divergence. One `Excel.run`. */
  async excel_converge(input: X.ConvergeInput): Promise<Json> {
    try {
      for (const a of [input.live_range, input.circ_range]) parseRange(a);
      parseCell(input.switch_cell);
      parseCell(input.delta_cell);
    } catch (e) {
      return invalidInput((e as Error).message);
    }
    const tolerance = Math.max(0, Number(input.tolerance ?? 0.01));
    const maxIterations = Math.min(500, Math.max(1, Math.floor(Number(input.max_iterations ?? 100))));
    try {
      return await Excel.run(async (ctx) => {
        const app = ctx.workbook.application;
        const iter = app.iterativeCalculation;
        iter.load('enabled');
        const ws = sheetOf(ctx, input.sheet);
        const live = ws.getRange(input.live_range);
        const circ = ws.getRange(input.circ_range);
        const delta = ws.getRange(input.delta_cell);
        circ.load('values');
        await ctx.sync();
        if (iter.enabled) {
          return { error: "Excel's iterative calculation is enabled: convergence must not run on top of it.", code: 'IterativeCalculationEnabled', retryable: false, hint: 'Ask the user to disable iterative calculation (File → Options → Formulas); the circ block + switch replaces it.' };
        }
        const snapshot = circ.values as X.CellValue[][];
        ws.getRange(input.switch_cell).values = [[1]];
        app.suspendScreenUpdatingUntilNextSync();
        const history: number[] = [];
        let verdict: 'converged' | 'diverged' | 'capped' | 'continue' = 'continue';
        let restored = false;
        for (let i = 0; i < maxIterations && verdict === 'continue'; i++) {
          live.load('values');
          await ctx.sync();
          circ.values = live.values;
          app.calculate('Full' as Excel.CalculationType);
          delta.load('values');
          await ctx.sync();
          const d = Number((delta.values as unknown[][])[0]?.[0]);
          history.push(Number.isFinite(d) ? d : NaN);
          verdict = assess(history, { tolerance, maxIterations });
        }
        if (verdict === 'continue') verdict = 'capped';
        if (verdict === 'diverged') {
          circ.values = snapshot;
          app.calculate('Full' as Excel.CalculationType);
          await ctx.sync();
          restored = true;
        }
        const out: X.ConvergeOutput = {
          status: 'ok',
          verdict,
          converged: verdict === 'converged',
          iterations: history.length,
          max_delta: history.length ? history[history.length - 1] : null,
          history: history.slice(-10),
          restored,
          _changes: changes(input.sheet, input.circ_range),
        };
        return out as unknown as Json;
      });
    } catch (err) {
      return toToolError(err, 'excel_converge');
    }
  }

  async excel_recalculate(input: X.RecalculateInput): Promise<Json> {
    try {
      return await Excel.run(async (ctx) => {
        ctx.workbook.application.calculate((input.mode === 'full' ? 'Full' : 'Recalculate') as Excel.CalculationType);
        await ctx.sync();
        return { status: 'ok', mode: input.mode ?? 'recalc' };
      });
    } catch (err) {
      return toToolError(err, 'excel_recalculate');
    }
  }

  async excel_set_calculation_mode(input: X.CalculationModeInput): Promise<Json> {
    if (input.mode !== 'automatic' && input.mode !== 'manual') return invalidInput('mode must be automatic or manual');
    try {
      return await Excel.run(async (ctx) => {
        const app = ctx.workbook.application;
        const wanted = input.mode === 'manual' ? Excel.CalculationMode.manual : Excel.CalculationMode.automatic;
        app.calculationMode = wanted;
        await ctx.sync();
        app.load('calculationMode');
        await ctx.sync();
        let observed = String(app.calculationMode).toLowerCase();
        if (observed !== input.mode) {
          // Some builds ignore the first assignment right after another application-level change;
          // one more attempt within the same run before reporting.
          app.calculationMode = wanted;
          await ctx.sync();
          app.load('calculationMode');
          await ctx.sync();
          observed = String(app.calculationMode).toLowerCase();
        }
        if (observed !== input.mode) {
          throw Object.assign(new Error(`Excel kept calculation mode '${observed}' after setting '${input.mode}'`), { code: 'InvalidOperation' });
        }
        return { status: 'ok', mode: input.mode, observed };
      });
    } catch (err) {
      return toToolError(err, 'excel_set_calculation_mode');
    }
  }
}

export const excelToolExecutor = new ExcelToolExecutor();
